// Pure helpers of the PV sizing tab: settings defaults, number formatting and
// the error texts, kept out of the component so they can be tested directly.
import { RANKING_DEFAULTS } from "../../components/config/autoDesignRanking";

// Flat DC derates compose multiplicatively in the engine (PVWatts):
// 100 * (1 - prod(1 - v/100)). Used to show the project's separate figures as one.
export function combineLossesPct(values) {
  const parts = values.filter((v) => v !== null && v !== undefined && v !== '').map(Number).filter((v) => Number.isFinite(v) && v >= 0 && v < 100);
  if (!parts.length) return null;
  return Math.round((1 - parts.reduce((acc, v) => acc * (1 - v / 100), 1)) * 100 * 100) / 100;
}

// Why each candidate size is on the list, in words a reader can act on. The
// kernel emits the keys; the text says what the size offers relative to the
// optimum, not how the algorithm found it.
export const CANDIDATE_REASONS = {
  optimum: ["Μέγιστο NPV", "Maximum NPV"],
  lower_investment: ["Μικρότερη επένδυση με NPV εντός της ανοχής", "Smaller investment with NPV within tolerance"],
  near_lower: ["Λίγο μικρότερη από τη βέλτιστη — έλεγχος ευστάθειας", "Slightly below the optimum — stability check"],
  near_upper: ["Λίγο μεγαλύτερη από τη βέλτιστη — έλεγχος ευστάθειας", "Slightly above the optimum — stability check"],
  band_lower: ["Κάτω όριο της οικονομικής ζώνης γύρω από τη βέλτιστη", "Lower edge of the economic band around the optimum"],
  band_upper: ["Άνω όριο της οικονομικής ζώνης γύρω από τη βέλτιστη", "Upper edge of the economic band around the optimum"],
  economic_band: ["Εντός της οικονομικής ζώνης NPV", "Within the economic NPV band"],
  local_peak: ["Δεύτερη κορυφή NPV σε άλλη περιοχή ισχύος", "Second NPV peak in another size region"],
  budget: ["Μέγιστη ισχύς εντός προϋπολογισμού", "Largest size within budget"],
  current_design: ["Η ισχύς του τρέχοντος σχεδιασμού", "The current design's size"],
  range_sample: ["Σημείο σύγκρισης στο εύρος αναζήτησης", "Comparison point across the search range"],
  ac_refinement: ["Επανέλεγχος με τον πραγματικό αντιστροφέα (Στάδιο 2)", "Re-check with the real inverter (stage 2)"],
  required: ["Ορίστηκε από τον χρήστη", "Set by the user"],
  pending: ["Θα προκύψει από το Στάδιο 1", "Determined by stage 1"],
};

// Short labels for the same keys, where a cell has no room for a sentence.
export const CANDIDATE_REASON_SHORT = {
  optimum: ["βέλτιστο", "optimum"], lower_investment: ["μικρότερη επένδυση", "smaller investment"],
  near_lower: ["κάτω γείτονας", "lower neighbour"], near_upper: ["άνω γείτονας", "upper neighbour"],
  band_lower: ["κάτω όριο οικονομικής ζώνης", "lower economic-band edge"], band_upper: ["άνω όριο οικονομικής ζώνης", "upper economic-band edge"],
  economic_band: ["εντός οικονομικής ζώνης", "within economic band"], local_peak: ["δεύτερη κορυφή", "second peak"],
  budget: ["όριο προϋπολογισμού", "budget limit"], current_design: ["τρέχων σχεδιασμός", "current design"],
  range_sample: ["σημείο σύγκρισης", "comparison point"], ac_refinement: ["επανέλεγχος AC", "AC re-check"],
  required: ["ορίστηκε από τον χρήστη", "set by the user"],
  pending: ["εκκρεμεί", "pending"],
};

/** What sets the user's required size apart, after its reason (" · …" each):
 * that the user set it, when it is also listed for another reason, and whether
 * it lies outside the search range or over the budget. "" for any other size. */
export function requiredNote(c, isGreek) {
  if (!c?.required) return "";
  const tr = (el, en) => (isGreek ? el : en);
  return [
    c.reason !== "required" ? tr("ορίστηκε από τον χρήστη", "set by the user") : null,
    c.outside_range ? tr("εκτός εύρους αναζήτησης", "outside the search range") : null,
    c.over_budget ? tr("πάνω από τον προϋπολογισμό — δεν προτείνεται", "over the budget — not recommended") : null,
  ].filter(Boolean).map((b) => ` · ${b}`).join("");
}

/** A designed plant producing far from the stage-1 estimate (pv_sizing_run.yield_check). */
export function yieldCheckText(check, isGreek) {
  if (!check) return "";
  const n = (v) => Number(v).toLocaleString(isGreek ? "el-GR" : "en-GB", { maximumFractionDigits: 0 });
  const dev = Number(check.deviation_pct);
  const signed = `${dev > 0 ? "+" : "−"}${n(Math.abs(dev))} %`;
  return isGreek
    ? `ειδική παραγωγή ${n(check.design_kwh_kwp)} kWh/kWp, ${signed} έναντι του Σταδίου 1 (${n(check.stage1_kwh_kwp)}) — ελέγξτε ότι το πλαίσιο ταιριάζει στην εγκατάσταση`
    : `specific yield ${n(check.design_kwh_kwp)} kWh/kWp, ${signed} against stage 1 (${n(check.stage1_kwh_kwp)}) — check that the module suits the installation`;
}

export function candidateReasonText(reason, isGreek, { short = false } = {}) {
  const table = short ? CANDIDATE_REASON_SHORT : CANDIDATE_REASONS;
  return (table[reason] || [reason, reason])[isGreek ? 0 : 1];
}

export const ERROR_TEXTS = {
  PV_SIZING_INVALID_TECHNICAL: ['Μια τεχνική παραδοχή του έργου (γωνίες, GCR, επιφάνεια, απώλειες, υποβάθμιση) έχει μη έγκυρη τιμή. Διορθώστε τη στο έργο και επαναλάβετε.', 'A technical assumption of the project (angles, GCR, surface, losses, degradation) has an invalid value. Fix it in the project and retry.'],
  PV_SIZING_PITCH_TOO_SHORT: ['Το βήμα σειρών του έργου είναι μικρότερο από το ύψος της σειράς με το πλαίσιο αναφοράς (πλαίσια καθ’ ύψος × μήκος πλαισίου). Αυξήστε το βήμα ή μειώστε τα πλαίσια καθ’ ύψος στη φυσική διάταξη.', 'The project’s row pitch is shorter than the row with the reference module (modules per row height × module length). Increase the pitch or reduce the modules per row height in the physical layout.'],
  PV_SIZING_INVALID_REFERENCE_GEOMETRY: ['Η γεωμετρία αναφοράς δεν είναι έγκυρη. Ελέγξτε τις διαστάσεις του πλαισίου και τον λόγο κάλυψης εδάφους (GCR).', 'Reference geometry is invalid. Check module dimensions and ground coverage ratio (GCR).'],
  PV_SIZING_NO_REFERENCE_MODULE: ['Δεν βρέθηκε κατάλληλο πλαίσιο αναφοράς στον διαθέσιμο κατάλογο. Επιλέξτε πλαίσιο στο πεδίο «Πλαίσιο αναφοράς Σταδίου 1» ή προσθέστε έγκυρο πλαίσιο στη βιβλιοθήκη.', 'No suitable reference module was found in the available catalogue. Select a Stage 1 reference module or add a valid module to the library.'],
  PV_SIZING_MODULE_REQUIRED: ['Το πλαίσιο του έργου δεν βρέθηκε στον κατάλογο. Επιλέξτε διαθέσιμο πλαίσιο στον εξοπλισμό του έργου.', 'The project module was not found in the catalogue. Select an available module in the project equipment.'],
  PV_SIZING_CHECKPOINT_VERSION: ['Το αποθηκευμένο Στάδιο 1 δεν είναι συμβατό με την τρέχουσα έκδοση. Εκτελέστε νέο υπολογισμό.', 'The saved Stage 1 is incompatible with this version. Run a new calculation.'],
  PV_SIZING_DESIGNED_OUTSIDE_CONSTRAINTS: ['Η σχεδίαση δεν ικανοποιεί τους επιλεγμένους περιορισμούς. Ελέγξτε τα φίλτρα και τα όρια της διαστασιολόγησης.', 'The design does not satisfy the selected constraints. Check sizing filters and limits.'],
  PV_SIZING_INVALID_EQUIPMENT_FILTERS: ['Ελέγξτε τις επιλογές στα φίλτρα εξοπλισμού.', 'Check the equipment filter selections.'],
  PV_SIZING_INVALID_EXPORT_LIMIT: ['Το όριο έγχυσης πρέπει να είναι έγκυρος μη αρνητικός αριθμός. Ελέγξτε τη Σύνδεση στο Δίκτυο.', 'The injection limit must be a valid non-negative number. Check Grid Connection.'],
  PV_SIZING_INVALID_PRIORITY: ['Ελέγξτε την προτεραιότητα επιλογής και την ανοχή οικονομικού οφέλους.', 'Check selection priority and financial benefit tolerance.'],
  PV_SIZING_INVALID_REFERENCE_WEATHER: ['Τα μετεωρολογικά δεδομένα αναφοράς δεν είναι έγκυρα. Ελέγξτε την πηγή και το έτος προσομοίωσης.', 'Reference weather data is invalid. Check the source and simulation year.'],
  PV_SIZING_NO_EQUIPMENT: ['Δεν βρέθηκε κατάλληλος εξοπλισμός με αυτά τα φίλτρα.', 'No suitable equipment matches these filters.'],
  PV_SIZING_LOCK_FILTER_CONFLICT: ['Ο κλειδωμένος εξοπλισμός δεν συμφωνεί με τα φίλτρα της διαστασιολόγησης.', 'Locked equipment conflicts with the sizing filters.'],
  PV_SIZING_INVALID_REFERENCE: ['Ελέγξτε τις παραδοχές του πλαισίου αναφοράς. Για διπλή όψη, ο συντελεστής πίσω όψης πρέπει να είναι από 1% έως 100%.', 'Check the reference module assumptions. For a bifacial module, bifaciality must be between 1% and 100%.'],
  PV_SIZING_LOCK_REQUIRED: ['Επιλέξτε εξοπλισμό στο έργο πριν ενεργοποιήσετε το κλείδωμα.', 'Select project equipment before enabling its lock.'],
  PV_SIZING_ORIENTATION_SHARES: ['Συμπληρώστε θετικά ποσοστά για όλους τους προσανατολισμούς, με άθροισμα 100%.', 'Enter positive shares for all orientations, totalling 100%.'],
  PV_SIZING_ORIENTATION_TRACKER: ['Η κατανομή σε πολλούς προσανατολισμούς υποστηρίζεται μόνο με σταθερή στήριξη.', 'Multiple orientations require fixed mounting.'],
  PV_SIZING_ORIENTATION_MPPTS: ['Η λύση δεν διαθέτει αρκετά ενεργά MPPT για όλους τους προσανατολισμούς.', 'The design has too few active MPPTs for all orientations.'],
  PV_SIZING_COST_REQUIRED: ['Συμπληρώστε τις επενδυτικές δαπάνες του έργου (κόστος εγκατάστασης) ή επιλέξτε «Ενιαίο κόστος €/kWp».', 'Fill in the project investment costs (installation) or choose “Single €/kWp cost”.'],
  PV_SIZING_SINGLE_COST_REQUIRED: ['Με «Ενιαίο κόστος €/kWp» συμπληρώστε το κόστος ανά kWp.', 'With “Single €/kWp cost”, enter the cost per kWp.'],
  PV_SIZING_OM_REQUIRED: ['Συμπληρώστε το ετήσιο ποσοστό λειτουργίας και συντήρησης, ακόμη και αν είναι μηδέν.', 'Enter annual O&M, including an explicit zero if appropriate.'],
  PV_SIZING_INVALID_REFERENCE_LOSSES: ['Οι απώλειες πρέπει να είναι από 0 έως λιγότερο από 100%.', 'Losses must be at least 0 and less than 100%.'],
  PV_SIZING_INVALID_SPLIT: ['Τα μερίδια κόστους πρέπει να είναι μη αρνητικά και να αθροίζουν σε 100%.', 'Cost shares must be non-negative and sum to 100%.'],
  PV_SIZING_INVALID_RANGE: ['Το άνω όριο ισχύος πρέπει να υπερβαίνει το κάτω, που πρέπει να είναι μη αρνητικό.', 'The upper size limit must exceed the non-negative lower limit.'],
  PV_SIZING_REFERENCE_MULTIPLE_ORIENTATIONS: ['Η αριθμητική αναφορά προς το παρόν υποστηρίζει έναν προσανατολισμό. Δεν έγινε υποκατάσταση των υπόλοιπων επιφανειών.', 'The numerical reference currently supports one orientation. Other planes were not silently substituted.'],
  PV_SIZING_NO_CHECKPOINT: ['Δεν υπάρχει διαθέσιμο ολοκληρωμένο πρώτο στάδιο για συνέχιση. Εκτελέστε νέο υπολογισμό.', 'No completed stage-1 checkpoint is available. Run a new calculation.'],
  PV_SIZING_INPUTS_CHANGED: ['Οι είσοδοι έχουν αλλάξει ή δεν μπορούν να επαληθευτούν. Εκτελέστε νέο υπολογισμό από το Στάδιο 1.', 'Inputs have changed or cannot be verified. Run a new calculation from stage 1.'],
  PV_SIZING_NO_HORIZON: [
    "Ορίστε τον ορίζοντα της μελέτης (έτη) στις Οικονομικές Παραδοχές.",
    "Set the study horizon (years) in the Financial Assumptions.",
  ],
  PV_SIZING_NO_DISCOUNT: [
    "Ορίστε ονομαστικό επιτόκιο προεξόφλησης μεγαλύτερο του μηδενός στις Οικονομικές Παραδοχές. Χωρίς προεξόφληση κάθε μεγαλύτερη εγκατάσταση φαίνεται καλύτερη και η διαστασιολόγηση δεν έχει νόημα.",
    "Set a nominal discount rate above zero in the Financial Assumptions. Without discounting every larger plant looks better and the sizing is meaningless.",
  ],
  PV_SIZING_NO_MODULE: [
    "Επιλέξτε φωτοβολταϊκό πλαίσιο στον εξοπλισμό του έργου για να προσομοιωθεί η παραγωγή.",
    "Select a PV module in the project equipment so generation can be simulated.",
  ],
  PV_SIZING_NO_YEAR: [
    "Ορίστε το έτος προσομοίωσης στα Μετεωρολογικά Δεδομένα.",
    "Set the simulation year in the Weather Data.",
  ],
  PV_SIZING_JOB_ACTIVE: [
    "Μια διαστασιολόγηση τρέχει ήδη για αυτό το έργο. Περιμένετε να ολοκληρωθεί ή ακυρώστε την.",
    "A sizing run is already active for this project. Wait for it or cancel it.",
  ],
  PV_SIZING_NO_PV: [
    "Το έργο δεν έχει φωτοβολταϊκό.",
    "The project has no PV asset.",
  ],
  PV_SIZING_NOT_BTM: [
    "Η διαστασιολόγηση αφορά έργα αυτοκατανάλωσης (πίσω από τον μετρητή).",
    "Sizing applies to behind-the-meter projects.",
  ],
};

export function weatherSourceLabel(source, isGreek) {
  if (!source) return '';
  if (source.kind === 'platform_internal' || source === 'Platform weather') return isGreek ? 'Μετεωρολογικά δεδομένα πλατφόρμας' : 'Platform weather';
  if (source.kind === 'imported_tmy') return isGreek ? 'Εισαγόμενο αρχείο TMY' : 'Imported TMY file';
  if (typeof source === 'string') return source;
  return typeof source.label === 'string' ? source.label : (isGreek ? 'Μετεωρολογικά δεδομένα αναφοράς' : 'Reference weather data');
}

export function sizingErrorText(error, isGreek) {
  const i = isGreek ? 0 : 1;
  if (error?.status === 403) {
    // The only refusal left: the account may not run feasibility computations
    // (api/services/demo_service: an expired or locked demo, or feasibility off).
    let code = error?.detail?.code;
    try { code = code || JSON.parse(error?.responseText || "{}")?.detail?.code; } catch { /* not JSON */ }
    if (code === "demo_expired") return isGreek ? "Η δοκιμαστική περίοδος του λογαριασμού έληξε· δεν επιτρέπονται νέοι υπολογισμοί." : "The account's demo period has expired; no new computations are allowed.";
    if (code === "demo_locked") return isGreek ? "Ο δοκιμαστικός λογαριασμός είναι κλειδωμένος· δεν επιτρέπονται νέοι υπολογισμοί." : "The demo account is locked; no new computations are allowed.";
    return isGreek ? "Η Μελέτη Σκοπιμότητας δεν είναι ενεργή για αυτόν τον λογαριασμό." : "Feasibility is not enabled for this account.";
  }
  let detail = typeof error === 'string' ? error : error?.responseText || error?.detail || error?.message || "";
  try { detail = JSON.parse(detail)?.detail ?? detail; } catch { /* plain text */ }
  if (typeof detail === "string" && ERROR_TEXTS[detail]) return ERROR_TEXTS[detail][i];
  if (String(detail).startsWith('PV_SIZING_INVALID_')) return isGreek ? 'Ελέγξτε τις αριθμητικές τιμές: δεν επιτρέπονται αρνητικά κόστη ή μη έγκυρα ποσοστά.' : 'Check numerical inputs: negative costs and invalid percentages are not allowed.';
  if (!isGreek && String(detail).includes('needs the peak-hour window')) return 'Select a single all-day rate or define the peak-hour window under Technical Assumptions → Tariffs.';
  if (!isGreek && String(detail).includes('enter the charge on netted energy')) return 'Under virtual net billing, enter the charge on netted energy (EUR/MWh) from your tariff. Enter zero only if this charge does not apply.';
  if (isGreek && String(detail).includes("needs the peak-hour window")) {
    return "Στις Τεχνικές Παραδοχές → Τιμολόγια επιλέξτε μία τιμή όλη την ημέρα ή ορίστε το ωράριο αιχμής για διαφορετικές τιμές αιχμής και εκτός αιχμής.";
  }
  if (isGreek && String(detail).includes("enter the charge on netted energy")) {
    return "Στις ρυθμίσεις εικονικού συμψηφισμού συμπληρώστε τη χρέωση συμψηφιζόμενης ενέργειας για ΛΠ1–3 και απώλειες (€/MWh), σύμφωνα με το τιμολόγιο. Εισαγάγετε 0 μόνο αν δεν υπάρχει αυτή η χρέωση.";
  }
  if (String(detail) === "PV_SIZING_EXPORT_LIMIT_MISMATCH") {
    return isGreek ? "Το όριο έγχυσης του έργου δεν συμφωνεί με αυτό που διαβάζει η προσομοίωση. Ελέγξτε τα όρια εισαγωγής και εξαγωγής στη Σύνδεση στο Δίκτυο." : "The project injection limit differs from the simulation limit. Check import and export limits under Grid Connection.";
  }
  if (/Stored spacingInfo|Physical layout info is missing/.test(String(detail))) return isGreek ? 'Η γεωμετρία που διαβάζει η προσομοίωση λείπει ή έχει ασύμβατες διαστάσεις και αποστάσεις σειρών. Ανοίξτε τις τεχνικές λεπτομέρειες για την ακριβή αιτία.' : 'The simulation geometry is missing or has inconsistent dimensions and row spacing. Open technical details for the exact cause.';
  if (/no longer available in the equipment/i.test(String(detail))) return isGreek ? 'Ο επιλεγμένος εξοπλισμός δεν είναι πλέον διαθέσιμος στον κατάλογο. Επιλέξτε διαθέσιμο εξοπλισμό ή άλλο πλαίσιο αναφοράς και εκτελέστε νέο υπολογισμό.' : 'The selected equipment is no longer available in the catalogue. Select available equipment or another reference module and run a new calculation.';
  if (/A flush roof-mounted array.*roofPitch.*roofAzimuth/s.test(String(detail))) return isGreek ? 'Για τοποθέτηση παράλληλα στη στέγη απαιτούνται η κλίση και το αζιμούθιο της στέγης. Ελέγξτε τα στοιχεία στέγης στις ρυθμίσεις ΦΒ και επαναλάβετε τον υπολογισμό.' : 'Flush roof mounting requires the roof pitch and azimuth. Check the roof details in the PV settings and run the calculation again.';
  if (/A flat DC shading loss of .*applied on top/s.test(String(detail))) return isGreek ? 'Η ποσοστιαία απώλεια σκίασης συνδυάζεται με γεωμετρικό υπολογισμό σκιάσεων χωρίς δήλωση ότι καλύπτει πρόσθετες σκιάσεις. Ελέγξτε τον τρόπο υπολογισμού σκίασης στις απώλειες ΦΒ: αν αφορά πρόσθετα εμπόδια, δηλώστε την ως πρόσθετη· διαφορετικά αφαιρέστε το ποσοστό που ήδη καλύπτεται.' : 'A flat shading loss is combined with geometric shading without being declared additional. Check the PV shading settings: declare the allowance as additional if it covers unmodelled obstructions, or remove the percentage already covered.';
  if (/CURRENT_CONFIG not set/.test(String(detail))) return isGreek ? 'Δεν αρχικοποιήθηκαν οι ρυθμίσεις της προσομοίωσης. Επαναλάβετε τον υπολογισμό και, αν επιμένει, επικοινωνήστε με την υποστήριξη.' : 'Simulation settings were not initialised. Retry and contact support if the problem persists.';
  if (/not enough area|modules fit/i.test(String(detail))) return isGreek ? 'Η διαθέσιμη επιφάνεια δεν επαρκεί για αυτή τη σχεδίαση. Ελέγξτε την επιφάνεια και τους περιορισμούς εξοπλισμού.' : 'The available area is insufficient for this design. Check area and equipment constraints.';
  // The platform weather service (Open-Meteo) is down or overloaded: nothing in the project is wrong.
  if (/open-meteo|weather[^\n]*could not be read|service is overloaded/i.test(String(detail))) return isGreek ? 'Η υπηρεσία μετεωρολογικών δεδομένων της πλατφόρμας δεν απαντά αυτή τη στιγμή. Τα στοιχεία του έργου είναι εντάξει· δοκιμάστε ξανά σε λίγα λεπτά.' : 'The platform weather service is not responding right now. The project inputs are fine; try again in a few minutes.';
  if (/failed to fetch|network error|networkerror/i.test(String(detail))) return isGreek ? 'Δεν ήταν δυνατή η σύνδεση με τον διακομιστή. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.' : 'Could not connect to the server. Check your connection and retry.';
  const base = isGreek ? "Η διαστασιολόγηση απέτυχε." : "Sizing failed.";
  // Unknown server exceptions remain in job diagnostics, never mixed into translated UI.
  return detail ? `${base} ${isGreek ? 'Δοκιμάστε ξανά και, αν το πρόβλημα επιμένει, επικοινωνήστε με την υποστήριξη.' : 'Retry and contact support if the problem persists.'}` : base;
}

export function normalizeSizingSettings(saved, cfg = null) {
  const s = saved && typeof saved === "object" ? saved : {};
  // Only what has no home elsewhere in the project. Earlier per-sizing copies of
  // project assumptions (technical, models, grant, O&M, reference module,
  // injection limit, kWp/module, cost split) are dropped on read: the project
  // is the one place they are edited.
  return {
    priority: s.priority === "lower_investment" ? "lower_investment" : "max_npv",
    npvTolerancePct: s.npvTolerancePct == null || s.npvTolerancePct === "" || !Number.isFinite(Number(s.npvTolerancePct)) ? 5 : Math.max(0, Math.min(20, Number(s.npvTolerancePct))),
    // "project": priced from the Economics page; "single": one €/kWp entered here.
    // Settings saved before the switch: an entered €/kWp meant "single".
    costMode: s.costMode === "single" || s.costMode === "project" ? s.costMode : (numOrEmpty(s.eurPerKwp) !== "" ? "single" : "project"),
    eurPerKwp: numOrEmpty(s.eurPerKwp),
    orientationShares: s.orientationShares && typeof s.orientationShares === 'object' ? { ...s.orientationShares } : {},
    fixedEur: numOrEmpty(s.fixedEur),
    rangeMode: s.rangeMode === "manual" ? "manual" : "auto",
    kMinKwp: numOrEmpty(s.kMinKwp),
    kMaxKwp: numOrEmpty(s.kMaxKwp),
    // The automatic range's upper end, as a share of the annual load (200 % = twice the consumption).
    autoMaxLoadPct: "autoMaxLoadPct" in s ? numOrEmpty(s.autoMaxLoadPct) : 200,
    candidates: Number.isFinite(Number(s.candidates)) && Number(s.candidates) > 0 ? Math.min(10, Math.round(Number(s.candidates))) : 5,
    budgetEur: numOrEmpty(s.budgetEur),
    // Which equipment the sizing designs with: the project's own, or the
    // catalogue. Unset means "the project's when it has both", see resolveEquipmentMode.
    equipmentMode: s.equipmentMode === "project" || s.equipmentMode === "catalogue" ? s.equipmentMode : null,
    // A size examined in both stages whatever the search finds (blank = none).
    // Settings saved before it existed carry the old stage-1 comparison size;
    // the field became this one, so that size is carried over.
    requiredKwp: "requiredKwp" in s ? numOrEmpty(s.requiredKwp)
      : (s.comparisonEnabled !== false && Number(s.referenceKwp) > 0 ? Number(s.referenceKwp) : ""),
    models: normalizeModels(s.models),
    reference: normalizeReference(s.reference, cfg),
    equipment: normalizeEquipment(s.equipment),
    autodesign: normalizeAutodesign(s.autodesign),
  };
}

function numOrEmpty(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
}


/** The equipment the sizing designs with. A project that has chosen its module
 * and inverter is sized with them unless the user asks for the catalogue:
 * that is the question every engineer asks first, and the sizing used to
 * answer it with a generic module and whatever the catalogue offered. */
export function projectEquipmentAvailable(cfg) {
  return Boolean(cfg?.read_module && cfg?.read_inverter);
}
export function resolveEquipmentMode(settings, cfg) {
  if (settings?.equipmentMode === "project" && !projectEquipmentAvailable(cfg)) return "catalogue";
  return settings?.equipmentMode || (projectEquipmentAvailable(cfg) ? "project" : "catalogue");
}
/** The settings as the run uses them: the equipment mode applied to the
 * stage-1 reference and the stage-2 locks. */
export function withEquipmentMode(settings, cfg) {
  if (resolveEquipmentMode(settings, cfg) !== "project") return settings;
  return {
    ...settings,
    reference: { ...settings.reference, module: "project" },
    autodesign: { ...settings.autodesign, lockModule: true, lockInverter: true },
  };
}

// What the request carries: blanks become null so the backend fills defaults.
export function settingsForRequest(settings) {
  const out = {};
  for (const [k, v] of Object.entries(settings)) {
    if (k === "autodesign" || k === "reference" || k === "equipment") out[k] = v;
    else if (k === "models") out.models = Object.fromEntries(Object.entries(v).map(([mk, mv]) => [mk, mv === "" ? null : mv]));
    else out[k] = v === "" ? null : v;
  }
  return out;
}

// The sizing writes numbers one way in both languages: comma for thousands,
// point for decimals (1,234.5). `isGreek` is kept in the signatures for the text.
export const NUMBER_LOCALE = "en-US";
export function fmtEur(v, isGreek, digits = 0) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return `${Number(v).toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: digits, minimumFractionDigits: digits })} €`;
}

export function fmtNum(v, isGreek, digits = 1) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

/** A size as the page shows it: whole kWp from 100 kWp up, one decimal below
 * (a 2.988,3 kWp optimum reads 2.988 kWp; a 7,4 kWp home keeps its decimal).
 * Display only: the computed sizes are unchanged. */
const kwpDigits = (v) => (Math.abs(Number(v)) >= 100 ? 0 : 1);
export function fmtKwp(v, isGreek) {
  const n = Number(v);
  return fmtNum(v, isGreek, Number.isFinite(n) ? kwpDigits(n) : 1);
}

export function fmtPct(v, isGreek, digits = 0) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return `${fmtNum(Number(v) * 100, isGreek, digits)} %`;
}

// IRR arrives in percent (14.3), not as a share; LCOE in €/kWh.
export function fmtIrr(v, isGreek) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return `${fmtNum(v, isGreek, 1)} %`;
}

export function fmtLcoe(v, isGreek, unit = true) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  return unit ? `${fmtNum(v, isGreek, 3)} €/kWh` : fmtNum(v, isGreek, 3);
}

export function fmtYears(v, isGreek) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return isGreek ? "> ορίζοντα" : "> horizon";
  return `${fmtNum(v, isGreek, 1)} ${isGreek ? "έτη" : "yrs"}`;
}

// ---------------------------------------------------------------------------
// Models and auto-design constraints dedicated to the sizing runs
// ---------------------------------------------------------------------------
export const PHASE_LABELS = {
  queued: ["Σε αναμονή στην ουρά…", "Queued…"],
  preparation: ["Προετοιμασία εισόδων (φορτίο, τιμολόγιο, τιμές)", "Preparing inputs (load, tariff, prices)"],
  reference_design: ["Σχεδιασμός εγκατάστασης αναφοράς", "Designing the reference plant"],
  reference_generation: ["Προσομοίωση έτους αναφοράς", "Simulating the reference year"],
  sweep: ["Βελτιστοποίηση ισχύος", "Optimising the size"],
  sensitivity: ["Ανάλυση ευαισθησίας", "Sensitivity analysis"],
  report: ["Σύνταξη αναφοράς", "Writing the report"],
  stage1_ready: ["Το Στάδιο 1 ολοκληρώθηκε — ξεκινά ο σχεδιασμός υποψηφίων", "Stage 1 done — designing candidates"],
  ranking: ["Κατάταξη υποψηφίων", "Ranking candidates"],
  design_sensitivity: ["Ευαισθησία της προτεινόμενης εγκατάστασης", "Sensitivity of the recommended plant"],
  project_design: ["Αξιολόγηση της σχεδίασης του έργου", "Evaluating the project's design"],
  completed: ["Ολοκληρώθηκε", "Completed"],
  cancelled: ["Ακυρώθηκε", "Cancelled"],
};

export function phaseLabel(message, isGreek) {
  const i = isGreek ? 0 : 1;
  const m = String(message || "queued");
  const sensitivity = m.match(/^sensitivity:(\d+)\/(\d+):(.+)$/);
  if (sensitivity) {
    const [, done, total, key] = sensitivity;
    const scenario = SCENARIO_LABELS[key]?.[i];
    return (isGreek ? `Ανάλυση ευαισθησίας — ${done}/${total} ολοκληρωμένα` : `Sensitivity — ${done}/${total} completed`) + (scenario ? ` · ${scenario}` : "");
  }
  const cand = m.match(/^candidate:(\d+)\/(\d+):(design|generation|done)(?::(\d+)\/(\d+))?$/);
  if (cand) {
    const [, k, n, step] = cand;
    const what = { design: ["αυτόματος σχεδιασμός", "auto-design"], generation: ["προσομοίωση έτους", "year simulation"], done: ["ολοκληρώθηκε", "done"] }[step][i];
    const alternative = cand[4] ? (isGreek ? ` · σχεδίαση ${cand[4]} από ${cand[5]}` : ` · design ${cand[4]} of ${cand[5]}`) : "";
    return (isGreek ? `Υποψήφια ${k} από ${n} — ${what}` : `Candidate ${k} of ${n} — ${what}`) + alternative;
  }
  return (PHASE_LABELS[m] || PHASE_LABELS.queued)[i];
}

// One sentence naming the assumption the recommendation hangs on. ``most`` is
// the kernel's ``sensitivity.most_sensitive``: the scenario that moves the
// optimum furthest from the base size.
export function mostSensitiveText(most, baseK, isGreek, fmt) {
  if (!most || !(baseK > 0) || !Number.isFinite(most.shift_kwp)) return null;
  const label = (SCENARIO_LABELS[most.key] || [most.key, most.key])[isGreek ? 0 : 1];
  const kwp = fmt ? fmt(Math.abs(most.shift_kwp), kwpDigits(most.shift_kwp)) : Math.abs(most.shift_kwp).toFixed(1);
  const pct = Number.isFinite(most.shift_pct) ? Math.abs(most.shift_pct).toFixed(0) : null;
  const dir = most.shift_kwp < 0 ? (isGreek ? "μικρότερη" : "smaller") : (isGreek ? "μεγαλύτερη" : "larger");
  const kStar = fmt ? fmt(most.k_star, kwpDigits(most.k_star)) : Number(most.k_star).toFixed(1);
  if (Math.abs(most.shift_kwp) < 1e-9) {
    return isGreek
      ? "Δεν βρέθηκε μεταβολή ισχύος στα σενάρια, εντός του εξεταζόμενου εύρους."
      : "No size change was found in the scenarios within the evaluated range.";
  }
  // An optimum of zero is not a smaller plant, it is no plant: say that, not
  // "447 kWp smaller (100 %), at 0.0 kWp".
  if (Number(most.k_star) < 1e-9) {
    return isGreek
      ? `Στο σενάριο «${label}» η επένδυση δεν αποδίδει σε καμία ισχύ: το βέλτιστο είναι να μη γίνει έργο (0 kWp).`
      : `In "${label}" no size pays off: the optimum is no plant at all (0 kWp).`;
  }
  // On the search limit the shift is a lower bound: the optimum may lie further out.
  if (most.boundary_limited) {
    return isGreek
      ? `Η μεγαλύτερη μεταβολή ισχύος εμφανίζεται στο σενάριο «${label}»: η ισχύς γίνεται τουλάχιστον ${kwp} kWp ${dir}${pct !== null ? ` (${pct} %)` : ""}, στο όριο αναζήτησης των ${kStar} kWp· η πραγματική βέλτιστη μπορεί να είναι πέρα από αυτό.`
      : `The largest size change occurs in "${label}": the size is at least ${kwp} kWp ${dir}${pct !== null ? ` (${pct} %)` : ""}, at the search limit of ${kStar} kWp; the true optimum may lie beyond it.`;
  }
  return isGreek
    ? `Η μεγαλύτερη μεταβολή ισχύος εμφανίζεται στο σενάριο «${label}»: η ισχύς γίνεται ${kwp} kWp ${dir}${pct !== null ? ` (${pct} %)` : ""}, στα ${kStar} kWp.`
    : `The largest size change occurs in "${label}": the size is ${kwp} kWp ${dir}${pct !== null ? ` (${pct} %)` : ""}, at ${kStar} kWp.`;
}

/** The conclusions the page states first, as the report does: what to build,
 * how equivalent the neighbours are, what the result hangs on, and where the
 * project's own design stands. Strings, in the page's language. */
export function sizingConclusions(s1, s2, isGreek, fmt) {
  const t = (el, en) => (isGreek ? el : en);
  const best = s2?.status === "completed" ? s2.best : null;
  const eur = (v) => fmtEur(v, isGreek);
  const out = [];
  if (!best?.evaluation) {
    const opt = s1?.optimum;
    if (opt && s1?.validity?.valid !== false) {
      out.push(t(`Εκτίμηση Σταδίου 1: βέλτιστη ισχύς ${fmtKwp(opt.k_kwp, isGreek)} kWp με NPV ${eur(opt.npv_eur)} και απόσβεση ${fmtYears(opt.payback_years, isGreek)}. Ο σχεδιασμός με πραγματικό εξοπλισμό (Στάδιο 2) δεν έχει τρέξει.`,
        `Stage-1 estimate: optimum size ${fmtKwp(opt.k_kwp, isGreek)} kWp with an NPV of ${eur(opt.npv_eur)} and a payback of ${fmtYears(opt.payback_years, isGreek)}. The design with real equipment (stage 2) has not run.`));
    }
    return out;
  }
  const ev = best.evaluation, d = best.design || {};
  out.push(t(`Προτείνεται εγκατάσταση ${fmtNum(d.actual_kwp, isGreek, 2)} kWp (${d.num_panels ?? "—"} × ${d.module || ""}, ${d.num_inverters ?? "—"} × ${d.inverter || ""}): NPV ${eur(best.npv_eur)}, IRR ${fmtIrr(ev.irr_pct, isGreek)}, απόσβεση ${fmtYears(ev.payback_years, isGreek)}, αυτοκατανάλωση ${fmtPct(ev.self_consumption_share, isGreek)}.`,
    `The recommended plant is ${fmtNum(d.actual_kwp, isGreek, 2)} kWp (${d.num_panels ?? "—"} × ${d.module || ""}, ${d.num_inverters ?? "—"} × ${d.inverter || ""}): NPV ${eur(best.npv_eur)}, IRR ${fmtIrr(ev.irr_pct, isGreek)}, payback ${fmtYears(ev.payback_years, isGreek)}, self-consumption ${fmtPct(ev.self_consumption_share, isGreek)}.`));
  const designed = (s2.candidates || []).filter((r) => ["designed", "drifted"].includes(r.status) && Number.isFinite(r.npv_eur) && r.design);
  const near = designed.filter((r) => best.npv_eur && r.npv_eur >= best.npv_eur - Math.abs(best.npv_eur) * 0.02);
  if (near.length > 1) {
    const sizes = near.map((r) => r.design.actual_kwp);
    out.push(t(`Οι σχεδιάσεις από ${fmtKwp(Math.min(...sizes), isGreek)} έως ${fmtKwp(Math.max(...sizes), isGreek)} kWp δίνουν NPV εντός 2 % της καλύτερης: μέσα σε αυτό το εύρος η επιλογή είναι θέμα επένδυσης και διαθέσιμου χώρου.`,
      `Designs from ${fmtKwp(Math.min(...sizes), isGreek)} to ${fmtKwp(Math.max(...sizes), isGreek)} kWp are within 2 % of the best NPV: within that range the choice is a matter of investment and available space.`));
  }
  const without = ev.npv_without_export_eur;
  if (Number.isFinite(without) && best.npv_eur > 0) {
    if (without < 0) out.push(t(`Χωρίς έσοδο από την εξαγωγή η NPV γίνεται −${eur(-without)}: η οικονομικότητα εξαρτάται από την αποζημίωση της ενέργειας που εξάγεται, η οποία πρέπει να επιβεβαιωθεί.`,
      `Without export revenue the NPV becomes −${eur(-without)}: the economics depend on how exported energy is paid, which needs confirming.`));
    else if (without < best.npv_eur * 0.7) out.push(t(`Χωρίς έσοδο από την εξαγωγή η NPV μειώνεται σε ${eur(without)}.`, `Without export revenue the NPV falls to ${eur(without)}.`));
  }
  const combined = s2.design_sensitivity?.combined_downside;
  if (combined && Number.isFinite(combined.npv_eur)) {
    const value = combined.npv_eur < 0 ? `−${eur(-combined.npv_eur)}` : eur(combined.npv_eur);
    out.push(t(`Αν συμβούν μαζί οι δυσμενείς μεταβολές του συνδυασμένου σεναρίου, η NPV της προτεινόμενης εγκατάστασης γίνεται ${value} και η απόσβεση ${fmtYears(combined.payback_years, isGreek)}.`,
      `If the combined downside's adverse shifts happen together, the recommended plant's NPV becomes ${value} and the payback ${fmtYears(combined.payback_years, isGreek)}.`));
  }
  const p90 = s2.design_sensitivity?.p90;
  if (p90 && Number.isFinite(p90.npv_eur) && Number.isFinite(p90.factor)) {
    const value = p90.npv_eur < 0 ? `−${eur(-p90.npv_eur)}` : eur(p90.npv_eur);
    const below = fmtNum(100 - p90.factor * 100, isGreek, 1);
    out.push(t(`Στο σενάριο P90 (όφελος ${below} % κάτω από το αναμενόμενο, από τις δηλωμένες αβεβαιότητες τιμολογίου, τιμής εξαγωγής, πόρου, κατανάλωσης και μοντέλου — ο ίδιος έλεγχος ανθεκτικότητας με τη μελέτη) η NPV γίνεται ${value} και η απόσβεση ${fmtYears(p90.payback_years, isGreek)}.`,
      `In the P90 case (benefit ${below} % below the expected, from the declared tariff, export-price, resource, load and model uncertainties — the same robustness test as the study) the NPV becomes ${value} and the payback ${fmtYears(p90.payback_years, isGreek)}.`));
  }
  const project = s2.project_design;
  if (project?.status === "evaluated" && Number.isFinite(project.npv_eur) && best.npv_eur) {
    const gap = project.npv_eur - best.npv_eur;
    out.push(t(`Η σχεδίαση του έργου (${fmtNum(project.design?.actual_kwp, isGreek, 2)} kWp) έχει NPV ${eur(project.npv_eur)}, ${fmtSignedEur(gap, isGreek)} έναντι της πρότασης (${fmtNum(Math.abs(gap) / Math.abs(best.npv_eur) * 100, isGreek, 1)} %).`,
      `The project's design (${fmtNum(project.design?.actual_kwp, isGreek, 2)} kWp) has an NPV of ${eur(project.npv_eur)}, ${fmtSignedEur(gap, isGreek)} against the recommendation (${fmtNum(Math.abs(gap) / Math.abs(best.npv_eur) * 100, isGreek, 1)} %).`));
  }
  return out;
}

export const SCENARIO_LABELS = {
  escalation_low: ["Κλιμάκωση τιμολογίου −2 π.μ.", "Tariff escalation −2 pp"],
  escalation_high: ["Κλιμάκωση τιμολογίου +2 π.μ.", "Tariff escalation +2 pp"],
  export_none: ["Χωρίς έσοδο εξαγωγής", "No export revenue"],
  export_low: ["Τιμή εξαγωγής −50 %", "Export price −50 %"],
  export_high: ["Τιμή εξαγωγής +50 %", "Export price +50 %"],
  load_low: ["Κατανάλωση −20 %", "Load −20 %"],
  load_high: ["Κατανάλωση +20 %", "Load +20 %"],
  cost_low: ["Κόστος −15 %", "Cost −15 %"],
  cost_high: ["Κόστος +15 %", "Cost +15 %"],
  discount_low: ["Προεξόφληση −2 π.μ.", "Discount rate −2 pp"],
  discount_high: ["Προεξόφληση +2 π.μ.", "Discount rate +2 pp"],
  yield_low: ["Παραγωγή −7 %", "Generation −7 %"],
  yield_high: ["Παραγωγή +7 %", "Generation +7 %"],
};

/** A signed amount: "+402.792 €", "−596.813 €". A change read without its
 * sign has to be decoded from the sentence around it. */
export function fmtSignedEur(v, isGreek) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  if (Math.abs(n) < 0.5) return fmtEur(0, isGreek);
  return `${n > 0 ? "+" : "−"}${fmtEur(Math.abs(n), isGreek)}`;
}

// One line per assumption in the tornado: the low and the high shift of the
// same assumption share it, so the chart shows how far each one swings the NPV.
export const SENSITIVITY_GROUP_LABELS = {
  escalation: ["Κλιμάκωση τιμολογίου ±2 π.μ.", "Tariff escalation ±2 pp"],
  export_none: ["Χωρίς έσοδο εξαγωγής", "No export revenue"],
  export: ["Τιμή εξαγωγής ±50 %", "Export price ±50 %"],
  load: ["Κατανάλωση ±20 %", "Load ±20 %"],
  cost: ["Κόστος ±15 %", "Cost ±15 %"],
  discount: ["Προεξόφληση ±2 π.μ.", "Discount rate ±2 pp"],
  yield: ["Παραγωγή ±7 %", "Generation ±7 %"],
};

/** The scenarios grouped by assumption and ordered by how far they move the
 * NPV at the base size, largest first: the tornado's rows, and the order the
 * table follows. Each scenario carries `change`, its NPV minus the base NPV. */
export function sensitivityTornado(sensitivity) {
  const base = Number(sensitivity?.base?.npv_eur);
  const groups = new Map();
  for (const r of sensitivity?.scenarios || []) {
    const change = Number.isFinite(r.npv_change_at_base_eur) ? r.npv_change_at_base_eur
      : (Number.isFinite(r.npv_at_base_k) && Number.isFinite(base) ? r.npv_at_base_k - base : null);
    const side = /_low$/.test(r.key) ? "low" : /_high$/.test(r.key) ? "high" : "single";
    const group = side === "single" ? r.key : r.key.replace(/_(low|high)$/, "");
    if (!groups.has(group)) groups.set(group, { group, rows: [] });
    groups.get(group).rows.push({ ...r, change, side });
  }
  const order = { low: 0, single: 1, high: 2 };
  return [...groups.values()].map((g) => {
    const rows = g.rows.sort((a, b) => order[a.side] - order[b.side]);
    const impact = Math.max(0, ...rows.map((r) => (Number.isFinite(r.change) ? Math.abs(r.change) : 0)));
    return { ...g, rows, impact };
  }).sort((a, b) => b.impact - a.impact);
}

export const DEMAND_SOURCE_LABELS = {
  metered_import: ["μετρημένο προφίλ (εισαγωγή)", "metered profile (import)"],
  modelled: ["μοντέλο κατανάλωσης της πλατφόρμας", "the platform's consumption model"],
};

/** What is measured and what is modelled: the two inputs the result rests on,
 * as the report states them. `ref` is stage 1's reference (demand_source,
 * weather_source) or an equivalent built from the project before a run. */
export function dataQuality(ref, isGreek) {
  const t = (el, en) => (isGreek ? el : en);
  const metered = ref?.demand_source === "metered_import";
  const tmy = ref?.weather_source?.kind === "imported_tmy";
  return [
    { key: "load", level: metered ? "good" : "fair", text: metered ? t("Κατανάλωση: μετρημένο προφίλ", "Load: metered profile") : t("Κατανάλωση: μοντέλο από μηνιαία στοιχεία", "Load: modelled from monthly figures"),
      hint: metered ? t("Το αποτέλεσμα στηρίζεται σε πραγματική, μετρημένη κατανάλωση.", "The result rests on real, metered consumption.")
        : t("Η κατανάλωση εκτιμάται από μηνιαία στοιχεία και τυπική μορφή ημέρας. Ένα μετρημένο προφίλ (εισαγωγή αρχείου στις Τεχνικές Παραδοχές) κάνει το αποτέλεσμα πιο αξιόπιστο.", "The load is estimated from monthly figures and a typical day shape. A metered profile (file import in the Technical Assumptions) makes the result more reliable.") },
    { key: "weather", level: tmy ? "good" : "fair", text: tmy ? t("Καιρός: τυπικό μετεωρολογικό έτος (TMY)", "Weather: typical meteorological year (TMY)") : t("Καιρός: ένα έτος μετεωρολογικών δεδομένων", "Weather: a single year of weather data"),
      hint: tmy ? t("Τυπικό μετεωρολογικό έτος: μια μέση χρονιά από πολυετή δεδομένα.", "Typical meteorological year: an average year from multi-year data.")
        : t("Ένα μόνο έτος μετεωρολογικών δεδομένων. Μια πιο ηλιόλουστη ή πιο συννεφιασμένη χρονιά από τον μέσο όρο μετακινεί την παραγωγή· ένα τυπικό μετεωρολογικό έτος (TMY) είναι πιο αντιπροσωπευτικό.", "A single year of weather data. A sunnier or cloudier year than average shifts the generation; a typical meteorological year (TMY) is more representative.") },
  ];
}

/** The one-sigma terms of the P90 budget, as the study lists them. */
export function p90TermsText(p90, isGreek) {
  const t = (el, en) => (isGreek ? el : en);
  const n = (v, d = 0) => fmtNum(v, isGreek, d);
  const share = Number(p90?.export_share_pct) || 0;
  const parts = [];
  if (Number.isFinite(Number(p90?.tariff_pct))) {
    parts.push(`${t("τιμολόγιο", "tariff")} ${n(p90.tariff_pct)} % × ${n(100 - share)} %`);
    if (share > 0.05) parts.push(`${t("τιμή εξαγωγής", "export price")} ${n(p90.price_pct)} % × ${n(share)} %`);
  }
  parts.push(`${t("πόρος", "resource")} ${n(p90?.resource_pct)} %`);
  if ((Number(p90?.load_pct) || 0) > 0.05) parts.push(`${t("κατανάλωση", "load")} ±${n(p90.load_pct)} %`);
  parts.push(`${t("μοντέλο", "model")} ${n(p90?.model_pct)} %`);
  return `${parts.join(" · ")} → σ ${n(p90?.sigma_pct, 1)} %`;
}

export const OPTION_LABELS = {
  economical: ["Οικονομική", "Economical"],
  max_npv: ["Μέγιστη NPV", "Maximum NPV"],
  self_sufficient: ["Μέγιστη αυτάρκεια", "Maximum self-sufficiency"],
};

/** The NPV tolerance the sizing was run with (the "lower investment"
 * criterion's setting, 5 % unless changed), from the result. */
export function sizingTolerancePct(s1, s2) {
  const candidates = [s1?.settings?.npvTolerancePct, s2?.selection?.npv_tolerance_pct];
  const found = candidates.find((v) => Number.isFinite(Number(v)) && v !== null && v !== "");
  return found === undefined ? 5 : Number(found);
}

/** Three ways to read the stage-2 designs, as the report lists them: the
 * largest NPV, the lowest investment that keeps the NPV within the sizing's
 * tolerance of it (the same tolerance as the "lower investment" criterion),
 * and the largest self-sufficiency among the profitable ones. A design that
 * wins two roles is listed once, with both. Empty when nothing profitable
 * was designed. */
export function proposalOptions(s2, tolerancePct = 5) {
  const rows = (s2?.candidates || []).filter((r) => ["designed", "drifted"].includes(r.status) && r.design && r.evaluation && Number.isFinite(r.npv_eur));
  if (!rows.length) return [];
  const best = rows.reduce((a, r) => (r.npv_eur > a.npv_eur ? r : a));
  if (best.npv_eur <= 0) return [];
  const capex = (r) => (Number.isFinite(r.evaluation.capex_gross_eur) ? r.evaluation.capex_gross_eur : Infinity);
  const floor = best.npv_eur * (1 - Math.min(Math.max(Number(tolerancePct) || 0, 0), 20) / 100);
  const economical = rows.filter((r) => r.npv_eur >= floor - 1e-6).reduce((a, r) => (capex(r) < capex(a) ? r : a));
  const autonomous = rows.filter((r) => r.npv_eur > 0).reduce((a, r) => ((r.evaluation.self_sufficiency_share || 0) > (a.evaluation.self_sufficiency_share || 0) ? r : a));
  const out = [];
  for (const [role, row] of [["economical", economical], ["max_npv", best], ["self_sufficient", autonomous]]) {
    const hit = out.find((o) => o.row === row);
    if (hit) hit.roles.push(role); else out.push({ roles: [role], row });
  }
  return out;
}

// The assumptions a reader can move on the page, with the range of each
// slider. Scales are multipliers of the project's value (1 = as set); the two
// rates are shifts in percentage points of the project's rate (0 = as set).
export const WHAT_IF_DIMENSIONS = [
  { dimension: "export_scale", label: ["Τιμή εξαγωγής", "Export price"], base: 1, min: 0, max: 1.5, step: 0.05, kind: "scale" },
  { dimension: "cost_scale", label: ["Κόστος επένδυσης", "Investment cost"], base: 1, min: 0.7, max: 1.3, step: 0.01, kind: "scale" },
  { dimension: "yield_scale", label: ["Παραγωγή", "Generation"], base: 1, min: 0.85, max: 1.15, step: 0.01, kind: "scale" },
  { dimension: "load_scale", label: ["Κατανάλωση", "Load"], base: 1, min: 0.7, max: 1.3, step: 0.01, kind: "scale" },
  { dimension: "retail_escalation", label: ["Κλιμάκωση τιμολογίου", "Tariff escalation"], base: 0, min: -0.03, max: 0.03, step: 0.005, kind: "points" },
  { dimension: "discount_rate", label: ["Προεξόφληση", "Discount rate"], base: 0, min: -0.03, max: 0.03, step: 0.005, kind: "points" },
];

/** The slider's position as text: "+20 %" for a scale, "+1,5 π.μ." for a rate. */
export function whatIfValueText(dim, value, isGreek) {
  if (dim.kind === "scale") {
    const pct = (value - 1) * 100;
    return Math.abs(pct) < 0.05 ? (isGreek ? "όπως στο έργο" : "as in the project") : `${pct > 0 ? "+" : "−"}${fmtNum(Math.abs(pct), isGreek, 0)} %`;
  }
  const pp = value * 100;
  return Math.abs(pp) < 0.005 ? (isGreek ? "όπως στο έργο" : "as in the project") : `${pp > 0 ? "+" : "−"}${fmtNum(Math.abs(pp), isGreek, 1)} ${isGreek ? "π.μ." : "pp"}`;
}

/** The NPV change of one assumption at `value`, read linearly between the
 * settled scenario points of that assumption (the base counts as a point with
 * no change); beyond the outermost point the last segment is extended. Null
 * when the sensitivity has no point for that assumption. */
export function whatIfChange(sensitivity, dim, value) {
  const points = (sensitivity?.scenarios || []).filter((r) => r.dimension === dim.dimension && Number.isFinite(r.value) && Number.isFinite(r.npv_change_eur))
    .map((r) => [r.value, r.npv_change_eur]);
  if (!points.length) return null;
  points.push([dim.base, 0]);
  points.sort((a, b) => a[0] - b[0]);
  const exact = points.find((p) => Math.abs(p[0] - value) < 1e-9);
  if (exact) return exact[1];
  let i = points.findIndex((p) => p[0] > value);
  if (i === 0) i = 1; else if (i < 0) i = points.length - 1;
  const [x0, y0] = points[i - 1], [x1, y1] = points[i];
  return x1 === x0 ? y0 : y0 + ((y1 - y0) * (value - x0)) / (x1 - x0);
}

/** The recommended plant's NPV with several assumptions moved at once: the
 * base NPV plus the sum of each assumption's own change. An estimate from the
 * settled scenarios, not a new simulation; the page says so beside it. */
export function whatIfEstimate(sensitivity, values) {
  const base = Number(sensitivity?.base?.npv_eur);
  if (!Number.isFinite(base)) return null;
  let change = 0;
  const parts = {};
  for (const dim of WHAT_IF_DIMENSIONS) {
    const value = values?.[dim.dimension];
    if (!Number.isFinite(value) || Math.abs(value - dim.base) < 1e-9) continue;
    const c = whatIfChange(sensitivity, dim, value);
    if (c === null) continue;
    parts[dim.dimension] = c;
    change += c;
  }
  return { npv_eur: base + change, change_eur: change, parts };
}

// Why a stage-1 result must not be read as a proposal (kernel.assess_validity).
export const VALIDITY_REASONS = {
  at_upper_bound: [
    "Η μεγαλύτερη NPV βρέθηκε στο άνω όριο. Δεν εντοπίστηκε εσωτερικό βέλτιστο· ελέγξτε το εύρος και τις παραδοχές εξαγωγής πριν επιλέξετε εγκατάσταση.",
    "The highest NPV was found at the upper boundary. No interior optimum was identified; review the range and export assumptions before selecting a plant.",
  ],
  no_discount_rate: [
    "Η προεξόφληση είναι 0 %: το NPV είναι απλό άθροισμα και ευνοεί κάθε μεγαλύτερη εγκατάσταση.",
    "The discount rate is 0 %: NPV is a plain sum and favours any larger plant.",
  ],
  self_consumption_too_low: [
    "Η αυτοκατανάλωση στο βέλτιστο είναι κάτω από 20 %: η εγκατάσταση είναι πάρκο πώλησης, όχι αυτοκατανάλωσης.",
    "Self-consumption at the optimum is below 20 %: the plant is a merchant plant, not a self-consumption one.",
  ],
  no_positive_npv: [
    "Καμία ισχύς στο εύρος δεν έχει θετικό NPV.",
    "No size in the range has a positive NPV.",
  ],
};

export const STATUS_LABELS = {
  pending: ["ΑΝΑΜΟΝΗ", "WAITING"],
  not_run: ["ΔΕΝ ΕΚΤΕΛΕΣΤΗΚΕ", "NOT RUN"],
  designing: ["ΣΧΕΔΙΑΣΜΟΣ", "DESIGNING"],
  simulating: ["ΠΡΟΣΟΜΟΙΩΣΗ", "SIMULATING"],
  designed: ["ΣΧΕΔΙΑΣΤΗΚΕ", "DESIGNED"],
  drifted: ["ΑΠΟΚΛΙΣΗ", "DRIFTED"],
  duplicate: ["ΔΙΠΛΟΤΥΠΟ", "DUPLICATE"],
  infeasible: ["ΜΗ ΕΦΙΚΤΟ", "INFEASIBLE"],
  over_budget: ["ΕΚΤΟΣ ΠΡΟΫΠΟΛΟΓΙΣΜΟΥ", "OVER BUDGET"],
};

// The stage-1 AC side is generic (PVWatts): a DC/AC ratio and a nominal
// efficiency. The generation models and wiring are the project's.
export function normalizeModels(saved) {
  const m = saved && typeof saved === "object" ? saved : {};
  const num = (v, d, lo, hi) => (Number.isFinite(Number(v)) && v !== "" && v !== null && Number(v) >= lo && Number(v) <= hi ? Number(v) : d);
  return { dcAcRatio: num(m.dcAcRatio, 1.15, 0.8, 2), referenceEfficiencyPct: num(m.referenceEfficiencyPct, 96, 50, 100) };
}

// The stage-1 reference module: the project's own when it has one, otherwise
// generic, described by the three datasheet numbers a sizing turns on, already
// filled with realistic figures. An explicit choice is kept.
export const GENERIC_MODULE_DEFAULTS = { powerW: 550, efficiencyPct: 21.5, tempCoeffPctPerC: -0.34 };
// The sizing's equipment prices (api/services/pv_sizing_cost.DEFAULT_EQUIPMENT):
// filled in for a new project, edited on the page; the catalogue's prices are not used.
export const EQUIPMENT_DEFAULTS = { moduleEurPerWp: 0.11, inverterEurPerWac: 0.05 };
export function normalizeEquipment(saved) {
  const e = saved && typeof saved === "object" ? saved : {};
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Number(v) : d);
  return {
    moduleEurPerWp: num(e.moduleEurPerWp, EQUIPMENT_DEFAULTS.moduleEurPerWp),
    inverterEurPerWac: num(e.inverterEurPerWac, EQUIPMENT_DEFAULTS.inverterEurPerWac),
  };
}
/** The stage-1 equipment price per W that the project's detailed economics
 * already gives (api/services/pv_sizing_cost.py reference_prices): the
 * entered price of the project's own model (per unit), else its catalogue
 * price. null for a component the project does not price: the sizing's own,
 * editable reference price applies to that one. */
export function projectEquipmentRates(cfg, details = {}) {
  const override = cfg?.feasibility?.economics?.equipmentCostOverride || {};
  const design = cfg?.systemDesign && typeof cfg.systemDesign === "object" ? cfg.systemDesign : {};
  let modules = 0;
  Object.values(design).forEach((mppts) => (Array.isArray(mppts) ? mppts : []).forEach((m) =>
    (Array.isArray(m?.inputs) ? m.inputs : []).forEach((i) => { const n = Number(i?.modules_per_string); if (n > 0) modules += Math.floor(n); })));
  const counts = { modules, inverters: Object.keys(design).length };
  const positive = (v) => { const n = Number(v); return v !== "" && v !== null && v !== undefined && Number.isFinite(n) && n > 0 ? n : null; };
  const entered = (v) => { const n = Number(v); return v !== "" && v !== null && v !== undefined && Number.isFinite(n) && n >= 0 ? n : null; };
  const installedKw = positive(cfg?.pvInstalledCap?.kw);
  // Why an amount entered in the economics is not the price here, so the page can say so.
  const unused = {};
  const rate = (name, chars, wattsKey, valueKey, nameKey, countKey) => {
    const watts = positive(chars?.[wattsKey]);
    const stamp = String(override[nameKey] || "");
    const value = entered(override[valueKey]);
    const note = (reason, extra = {}) => { if (value !== null) unused[countKey === "modules" ? "module" : "inverter"] = { value, reason, ...extra }; };
    if (!name) { note("no_equipment"); return null; }
    if (!watts) { note("no_rating"); return null; }
    if (value !== null && stamp && stamp !== name) note("other_model");
    else if (value !== null && counts[countKey] > 0) return { rate: value / counts[countKey] / watts, source: "override" };
    else if (value !== null && countKey === "modules" && installedKw) {
      // No electrical design yet: the module total over the installed power, if plausible
      // (api/services/pv_sizing_cost.module_rate_from_capacity).
      const spread = value / (installedKw * 1000);
      if (spread >= MODULE_RATE_FROM_CAPACITY[0] && spread <= MODULE_RATE_FROM_CAPACITY[1]) return { rate: spread, source: "override_capacity", installedKw };
      note("implausible", { rate: spread, installedKw });
    } else if (value !== null) note("no_design");
    const price = positive(chars?.Price);
    return price ? { rate: price / watts, source: "catalogue" } : null;
  };
  return {
    module: rate(String(cfg?.read_module || ""), details.module, "STC", "modules", "moduleName", "modules"),
    inverter: rate(String(cfg?.read_inverter || ""), details.inverter, "Paco", "inverters", "inverterName", "inverters"),
    unused,
  };
}

export const MODULE_RATE_FROM_CAPACITY = [0.05, 1.0];

/** One sentence on why an economics amount is not the stage-1 price. */
export function unusedPriceText(u, component, isGreek) {
  if (!u) return "";
  const what = component === "module" ? (isGreek ? "πλαίσια" : "modules") : (isGreek ? "αντιστροφείς" : "inverters");
  const amount = fmtEur(u.value, isGreek);
  const why = {
    no_equipment: ["το έργο δεν έχει επιλεγμένο εξοπλισμό, άρα δεν είναι γνωστό σε πόσα W αντιστοιχεί", "the project has no equipment chosen, so it is not known how many W it covers"],
    no_rating: ["ο κατάλογος δεν δίνει την ισχύ του μοντέλου", "the catalogue gives no rating for the model"],
    other_model: ["καταχωρίστηκε για άλλο μοντέλο από το επιλεγμένο", "it was entered for another model than the one chosen"],
    no_design: ["χωρίς αποθηκευμένο ηλεκτρολογικό σχεδιασμό δεν είναι γνωστό το πλήθος", "without a saved electrical design the count is not known"],
    implausible: [`διαιρούμενο με την εγκατεστημένη ισχύ (${fmtNum(u.installedKw, isGreek, 2)} kWp) δίνει ${fmtNum(u.rate, isGreek, 3)} €/Wp, εκτός λογικών ορίων`,
      `over the installed power (${fmtNum(u.installedKw, isGreek, 2)} kWp) it gives ${fmtNum(u.rate, isGreek, 3)} €/Wp, outside plausible bounds`],
  }[u.reason] || ["", ""];
  return isGreek ? `Το ποσό των οικονομικών παραδοχών για ${what} (${amount}) δεν χρησιμοποιείται: ${why[0]}.`
    : `The economics amount for ${what} (${amount}) is not used: ${why[1]}.`;
}

export function normalizeReference(saved, cfg = null) {
  const r = saved && typeof saved === "object" ? saved : {};
  const explicit = ["project", "generic", "generic_bifacial"].includes(r.module) ? r.module : null;
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Number(v) : d);
  return {
    module: explicit || (String(cfg?.read_module || "").trim() ? "project" : "generic"),
    powerW: num(r.powerW, GENERIC_MODULE_DEFAULTS.powerW),
    efficiencyPct: num(r.efficiencyPct, GENERIC_MODULE_DEFAULTS.efficiencyPct),
    tempCoeffPctPerC: num(r.tempCoeffPctPerC, GENERIC_MODULE_DEFAULTS.tempCoeffPctPerC),
    ...(r.module === "generic_bifacial" ? { bifacialityPct: num(r.bifacialityPct, 70) } : {}),
  };
}

/** What stage 1 reads from the project's module, from its catalogue entry:
 * power, efficiency (power ÷ area), power temperature coefficient, size and
 * rear-side coefficient. Each null when the catalogue does not give it. */
export function projectModuleFacts(chars) {
  if (!chars || typeof chars !== "object") return null;
  const n = (v) => { const x = Number(v); return v !== null && v !== undefined && v !== "" && Number.isFinite(x) ? x : null; };
  const powerW = n(chars.STC) ?? n(chars.pdc0);
  const areaM2 = n(chars.Area ?? chars.area);
  const lengthM = n(chars.Length ?? chars.length), widthM = n(chars.Width ?? chars.width);
  // gamma_r is %/°C in the catalogue; gamma_pdc a fraction per °C.
  const tempCoeffPctPerC = n(chars.gamma_r) ?? (n(chars.gamma_pdc) !== null ? n(chars.gamma_pdc) * 100 : null);
  const bifacial = Number(chars.Bifacial) > 0 || n(chars.bifaciality) > 0;
  return {
    powerW,
    efficiencyPct: powerW > 0 && areaM2 > 0 ? powerW / (areaM2 * 10) : null,
    tempCoeffPctPerC,
    lengthM: lengthM > 0 ? lengthM : null, widthM: widthM > 0 ? widthM : null,
    bifacialityPct: bifacial && n(chars.bifaciality) > 0 ? n(chars.bifaciality) * 100 : null,
    bifacial,
  };
}

/** The generic module's size (api/services/pv_sizing_reference.generic_module_dimensions):
 * area = power ÷ (1000 W/m² × efficiency), a 2:1 portrait module. */
export function genericModuleDimensions(powerW, efficiencyPct) {
  const p = Number(powerW); const e = Number(efficiencyPct);
  if (!(p > 0 && e > 0)) return null;
  const areaM2 = p / (1000 * e / 100);
  const widthM = Math.sqrt(areaM2 / 2);
  return { areaM2, lengthM: 2 * widthM, widthM };
}

const finiteOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The project's rows as the sizing reads them (pv_sizing_config.resolve_technical):
 * its pitch wins (a stored GCR belongs to whichever module was selected then);
 * a GCR without a pitch is kept; neither means the platform's rule. */
export function projectRowSpacing(cfg) {
  const s = cfg?.pv_design?.spacingInfo || {};
  const rowsRaw = finiteOrNull(s.row_vertical_modules);
  const rows = rowsRaw !== null && Number.isInteger(rowsRaw) && rowsRaw > 0 ? rowsRaw : 1;
  const flush = String(cfg?.panelOrientation?.installationType || "") === "roof_flush";
  const base = { rows, rowsSet: rowsRaw !== null, flush, pitchM: null, gcr: null };
  const pitchM = [s.minimum_distance_m, s.pitch_m, s.pitch].map(finiteOrNull).find((v) => v !== null) ?? null;
  if (pitchM !== null && pitchM > 0) return { ...base, mode: "pitch", pitchM };
  const gcr = finiteOrNull(s.gcr);
  if (gcr !== null && gcr > 0) return { ...base, mode: "gcr", gcr };
  return { ...base, mode: "rule" };
}

/** What stage 1 assumes for the rows (pv_sizing_reference.reference_layout): the
 * reference module's size and the ground coverage the project's pitch gives it.
 * A project module's size and the rule's pitch are known only after a run:
 * ``last`` is the previous run's layout, used when it was made the same way. */
export function referenceLayout(cfg, reference, last = null, lastModule = null, projectModule = null) {
  const spacing = projectRowSpacing(cfg);
  // The project's module, when its catalogue entry gives its size, is known
  // before a run too: its length sets the coverage the project's pitch gives.
  const own = reference?.module === "project" && projectModule?.lengthM > 0 && projectModule?.widthM > 0
    ? { lengthM: projectModule.lengthM, widthM: projectModule.widthM, areaM2: null } : null;
  const dims = ["generic", "generic_bifacial"].includes(reference?.module) ? genericModuleDimensions(reference.powerW, reference.efficiencyPct) : own;
  const out = { ...spacing, moduleLengthM: dims?.lengthM ?? null, moduleWidthM: dims?.widthM ?? null,
    moduleAreaM2: dims?.areaM2 ?? null, measured: false, projectSize: Boolean(own) };
  if (spacing.mode === "pitch" && dims) out.gcr = spacing.rows * dims.lengthM / spacing.pitchM;
  const same = last && last.measured && last.mode === spacing.mode && Number(last.rows) === spacing.rows
    && lastModule === (reference?.module || "generic")
    && (spacing.mode !== "pitch" || Math.abs(Number(last.pitchM) - spacing.pitchM) < 1e-6)
    && (!dims || Math.abs(Number(last.moduleLengthM) - dims.lengthM) < 1e-3);
  if (same && (out.gcr === null || spacing.mode === "rule")) {
    Object.assign(out, { gcr: Number(last.gcr) || null, measured: true, fallback: Boolean(last.fallback),
      moduleLengthM: out.moduleLengthM ?? last.moduleLengthM ?? null, moduleWidthM: out.moduleWidthM ?? last.moduleWidthM ?? null });
    if (spacing.mode === "rule") out.pitchM = finiteOrNull(last.pitchM);
  }
  return out;
}

/** The lines the page shows under the stage-1 reference: module size, then the rows. */
export function referenceLayoutText(layout, isGreek, fmt) {
  if (!layout) return [];
  const lines = [];
  const pct = (v) => `${fmt(v * 100, 1)} %`;
  const up = (n) => (isGreek ? `${n} ${n === 1 ? "πλαίσιο" : "πλαίσια"} καθ’ ύψος` : `${n} module${n === 1 ? "" : "s"} per row height`);
  const last = isGreek ? " (τελευταίος υπολογισμός)" : " (last run)";
  if (layout.moduleAreaM2) {
    lines.push(isGreek
      ? `Διαστάσεις γενικού πλαισίου: ${fmt(layout.moduleLengthM, 2)} × ${fmt(layout.moduleWidthM, 2)} m (${fmt(layout.moduleAreaM2, 2)} m² = ισχύς ÷ (1000 W/m² × απόδοση), αναλογία 2:1, κατακόρυφα).`
      : `Generic module size: ${fmt(layout.moduleLengthM, 2)} × ${fmt(layout.moduleWidthM, 2)} m (${fmt(layout.moduleAreaM2, 2)} m² = power ÷ (1000 W/m² × efficiency), 2:1, portrait).`);
  } else if ((layout.measured || layout.projectSize) && layout.moduleLengthM && layout.moduleWidthM) {
    lines.push(isGreek
      ? `Διαστάσεις πλαισίου του έργου: ${fmt(layout.moduleLengthM, 2)} × ${fmt(layout.moduleWidthM, 2)} m.`
      : `Project module size: ${fmt(layout.moduleLengthM, 2)} × ${fmt(layout.moduleWidthM, 2)} m.`);
  }
  if (layout.flush) {
    lines.push(isGreek ? "Επίπεδη τοποθέτηση στη στέγη: η επιφάνεια καλύπτεται πλήρως." : "Flush-mounted on the roof: the surface is fully covered.");
    return lines;
  }
  if (layout.mode === "pitch") {
    lines.push(layout.gcr && layout.moduleLengthM
      ? (isGreek
        ? `Κάλυψη εδάφους: ${up(layout.rows)} × ${fmt(layout.moduleLengthM, 2)} m ÷ βήμα σειρών του έργου ${fmt(layout.pitchM, 2)} m = ${pct(layout.gcr)}${layout.measured ? last : ""}. Στο Στάδιο 2 κάθε σχέδιο κρατά το ίδιο βήμα, με το μήκος του δικού του πλαισίου.`
        : `Ground coverage: ${up(layout.rows)} × ${fmt(layout.moduleLengthM, 2)} m ÷ the project’s row pitch ${fmt(layout.pitchM, 2)} m = ${pct(layout.gcr)}${layout.measured ? last : ""}. In stage 2 each design keeps the same pitch, with its own module’s length.`)
      : (isGreek
        ? `Κάλυψη εδάφους: ${up(layout.rows)} × μήκος του πλαισίου του έργου ÷ βήμα σειρών του έργου ${fmt(layout.pitchM, 2)} m (υπολογίζεται στην εκτέλεση). Στο Στάδιο 2 κάθε σχέδιο κρατά το ίδιο βήμα.`
        : `Ground coverage: ${up(layout.rows)} × the project module’s length ÷ the project’s row pitch ${fmt(layout.pitchM, 2)} m (computed in the run). In stage 2 each design keeps the same pitch.`));
  } else if (layout.mode === "gcr") {
    lines.push(isGreek
      ? `Κάλυψη εδάφους του έργου: ${pct(layout.gcr)} · ${up(layout.rows)} (χωρίς βήμα σειρών· ισχύει και στο Στάδιο 2).`
      : `The project’s ground coverage: ${pct(layout.gcr)} · ${up(layout.rows)} (no row pitch; stage 2 keeps it too).`);
  } else {
    lines.push(isGreek
      ? `Το έργο δεν ορίζει βήμα σειρών: η απόσταση προκύπτει από τον κανόνα της πλατφόρμας (χωρίς σκίαση στο χειμερινό ηλιοστάσιο 09:00–15:00, ελάχιστος διάδρομος 0,5 m), όπως και στα σχέδια του Σταδίου 2 · ${up(layout.rows)}.`
      : `The project sets no row pitch: the spacing follows the platform rule (no shading at the winter solstice 09:00–15:00, at least a 0.5 m aisle), as in the stage-2 designs · ${up(layout.rows)}.`);
    if (layout.measured) {
      lines.push(layout.fallback
        ? (isGreek ? "Στον τελευταίο υπολογισμό ο κανόνας δεν υπολογίστηκε· χρησιμοποιήθηκε κάλυψη 35 %." : "In the last run the rule could not be evaluated; 35 % coverage was used.")
        : (isGreek ? `Στον τελευταίο υπολογισμό: βήμα ${fmt(layout.pitchM, 2)} m → κάλυψη ${pct(layout.gcr)}.` : `In the last run: pitch ${fmt(layout.pitchM, 2)} m → coverage ${pct(layout.gcr)}.`));
    }
  }
  return lines;
}

/** Stage 1's evenly spaced first pass over the search range (sweep `grid_k`;
 * rebuilt from the range for results saved before it was returned), with the
 * candidates and the optimum among them, smallest first. */
export function stage1Points(s1) {
  const curve = Array.isArray(s1?.curve) ? s1.curve : [];
  if (!curve.length) return [];
  const range = s1.range || {};
  let grid = Array.isArray(s1.grid_k) ? s1.grid_k : null;
  if (!grid && Number(range.grid_points) >= 3 && Number.isFinite(range.k_min_kwp) && Number.isFinite(range.k_max_kwp)) {
    const n = Math.round(Number(range.grid_points));
    grid = Array.from({ length: n }, (_, i) => range.k_min_kwp + (range.k_max_kwp - range.k_min_kwp) * i / (n - 1));
  }
  const near = (a, b) => Math.abs(a - b) <= Math.max(1e-6, Math.abs(b) * 1e-9);
  const rows = new Map();
  const put = (p, patch) => {
    if (!p || !Number.isFinite(p.k_kwp)) return;
    const key = [...rows.keys()].find((k) => near(k, p.k_kwp)) ?? p.k_kwp;
    rows.set(key, { ...p, ...rows.get(key), ...patch });
  };
  (grid || []).forEach((k) => put(curve.find((p) => near(p.k_kwp, k)), { grid: true }));
  (s1.candidates || []).forEach((c) => put(c, { candidate: true, reason: c.reason }));
  put(s1.optimum, { optimum: true });
  return [...rows.values()].sort((a, b) => a.k_kwp - b.k_kwp);
}

export function sizingInputKey(value) {
  if (Array.isArray(value)) return `[${value.map(sizingInputKey).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.keys(value).sort().filter((k) => value[k] !== undefined).map((k) => `${JSON.stringify(k)}:${sizingInputKey(value[k])}`).join(",")}}`;
  return JSON.stringify(value);
}

// The project fields the sizing reads (the backend's study keys and its own),
// so that a change anywhere else - the project's name, its client, a report
// language, the design the sizing itself applied, a saved layout - does not
// make a result "stale". Everything the sizing does not read is left out.
const SIZING_PROJECT_KEYS = ["settlementType", "systemType", "roof_area", "loc_info", "useCase", "surfaceType", "albedo", "annualDegFactor", "pvLosses",
  "connectionCurtailment", "marketParticipation", "btm", "grid", "allowGridInjection", "simultaneousInjectDraw", "demand", "con_dets",
  "timeRange_weekdays", "timeRange_saturday", "timeRange_sunday", "dailyWeigts", "inptTariffs", "peakWindow", "energySettlement", "is_single_zonal",
  "is_billing", "carryover_enabled", "demandProjection", "tariffs", "weather", "weatherSource", "tmySource", "tmy", "tmyImport", "meteoSource",
  "simulationYear", "settlementPeriod", "timeZone", "biddingZone"];
const SIZING_ECONOMICS_KEYS = ["financialParams", "investmentCosts", "ppa", "equipmentCostOverride"];
const num2 = (v) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Math.round(Number(v) * 100) / 100 : null);

/** The sizing's view of a project: only what a run reads, in a comparable form. */
export function sizingInputs(cfg) {
  if (!cfg) return null;
  const settings = normalizeSizingSettings(cfg.pvSizing, cfg);
  const po = cfg.panelOrientation || {};
  const flush = String(po.installationType || "") === "roof_flush";
  const spacing = (cfg.pv_design && cfg.pv_design.spacingInfo) || {};
  const out = {};
  for (const key of SIZING_PROJECT_KEYS) if (cfg[key] !== undefined) out[key] = cfg[key];
  // The auto-design ranking the sizing also uses; only a choice away from the
  // defaults is recorded, so results saved before it existed stay current.
  const ranking = Object.fromEntries(Object.entries(RANKING_DEFAULTS)
    .filter(([key, fallback]) => typeof cfg.autoDesign?.[key] === "boolean" && cfg.autoDesign[key] !== fallback)
    .map(([key]) => [key, cfg.autoDesign[key]]));
  if (Object.keys(ranking).length) out.autoDesignRanking = ranking;
  const feas = cfg.feasibility || {};
  out.feasibility = {
    simulationYear: feas.simulationYear, operatingStartYear: feas.operatingStartYear, settlementPeriod: feas.settlementPeriod,
    weather: feas.weather, tmySource: feas.tmySource, technical: feas.technical,
    economics: Object.fromEntries(SIZING_ECONOMICS_KEYS.filter((k) => (feas.economics || {})[k] !== undefined).map((k) => [k, feas.economics[k]])),
  };
  // The planes: their geometry, not the layout drawn on them (sceneViews, spacing, shading state).
  out.orientations = Array.isArray(cfg.orientations)
    ? cfg.orientations.map(({ spacingInfo, sceneViews, obstacleShading, ...plane }) => plane) : undefined;
  // The modules' mounting: the numbers the run resolves, not the whole block.
  const { sceneViews, ...mounting } = po;
  out.panelOrientation = mounting;
  // Rows and pitch feed the run only on rows (ground and tilted roofs); a flush
  // array has no row spacing, so the pitch an applied design records is no input.
  out.rows = num2(spacing.row_vertical_modules) || 1;
  // A pitch the sizing wrote under the platform rule (an applied recommendation) is the rule's result, not a target.
  const ruled = spacing.source === "pv_sizing" && spacing.from_rule === true;
  out.pitchM = null;
  out.gcr = null;
  if (!flush && !ruled) {
    const pitch = [spacing.minimum_distance_m, spacing.pitch_m, spacing.pitch].map(num2).find((v) => v !== null);
    out.pitchM = pitch === undefined ? null : pitch;
    out.gcr = num2(spacing.gcr);
  }
  // The project's own module and inverter matter only when the sizing designs with them.
  // ...and the module also when stage 1 takes it as its reference.
  if (resolveEquipmentMode(settings, cfg) === "project" || settings.autodesign.lockModule || settings.reference.module === "project") out.read_module = cfg.read_module;
  if (resolveEquipmentMode(settings, cfg) === "project" || settings.autodesign.lockInverter) out.read_inverter = cfg.read_inverter;
  // The equipment mode as the run resolves it: the setting left blank reads as
  // "the project's" only once the project has equipment, so the resolved mode
  // is the input, not the blank.
  out.pvSizing = { ...settings, equipmentMode: resolveEquipmentMode(settings, cfg) };
  return out;
}

/** Whether a run's inputs still match the project: compared on the sizing's
 * own view of it (sizingInputs), with the settings in their normalised form,
 * so a project saved by an earlier version is not "changed" by the first edit
 * on this page, a value edited and put back reads as unchanged, and applying
 * the recommended design, downloading a report or renaming the project do not
 * declare the result out of date. */
export function sizingInputsMatch(inputConfig, cfg) {
  if (!inputConfig || !cfg) return false;
  return sizingInputKey(sizingInputs(inputConfig)) === sizingInputKey(sizingInputs(cfg));
}

const INPUT_GROUP_LABELS = {
  loc_info: ["τοποθεσία", "location"], roof_area: ["διαθέσιμη επιφάνεια", "available area"], surfaceType: ["επιφάνεια εδάφους", "surface"], albedo: ["ανακλαστικότητα", "albedo"],
  annualDegFactor: ["υποβάθμιση ΦΒ", "PV degradation"], pvLosses: ["απώλειες ΦΒ", "PV losses"], panelOrientation: ["κλίση/προσανατολισμός πλαισίων", "module tilt/azimuth"],
  rows: ["σειρές πλαισίων", "module rows"], pitchM: ["απόσταση σειρών", "row pitch"], gcr: ["κάλυψη εδάφους (GCR)", "ground coverage (GCR)"],
  orientations: ["επίπεδα εγκατάστασης", "installation planes"], read_module: ["πλαίσιο του έργου", "project module"], read_inverter: ["αντιστροφέας του έργου", "project inverter"],
  pvSizing: ["ρυθμίσεις διαστασιολόγησης", "sizing settings"], settlementType: ["τύπος εκκαθάρισης", "settlement type"], systemType: ["τύπος συστήματος", "system type"],
  useCase: ["χρήση", "use case"], btm: ["τιμολόγιο/αποζημίωση", "tariff/compensation"], grid: ["δίκτυο", "grid"], allowGridInjection: ["έγχυση στο δίκτυο", "grid injection"],
  connectionCurtailment: ["περικοπή σύνδεσης", "connection curtailment"], demand: ["κατανάλωση", "load"], demandProjection: ["προβολή κατανάλωσης", "load projection"],
  tariffs: ["τιμολόγια", "tariffs"], inptTariffs: ["τιμολόγια", "tariffs"], peakWindow: ["ώρες αιχμής", "peak hours"], energySettlement: ["εκκαθάριση ενέργειας", "energy settlement"],
  weather: ["καιρός", "weather"], weatherSource: ["πηγή καιρού", "weather source"], tmySource: ["TMY", "TMY"], tmy: ["TMY", "TMY"], tmyImport: ["TMY", "TMY"], meteoSource: ["πηγή καιρού", "weather source"],
  simulationYear: ["έτος προσομοίωσης", "simulation year"], settlementPeriod: ["ορίζοντας", "horizon"],
  "feasibility.simulationYear": ["έτος προσομοίωσης", "simulation year"], "feasibility.settlementPeriod": ["ορίζοντας", "horizon"], "feasibility.operatingStartYear": ["έτος έναρξης", "start year"],
  "feasibility.weather": ["καιρός", "weather"], "feasibility.tmySource": ["TMY", "TMY"], "feasibility.technical": ["τεχνικές παραδοχές (κατανάλωση, τιμολόγιο)", "technical assumptions (load, tariff)"],
  "feasibility.economics.financialParams": ["οικονομικές παράμετροι", "financial parameters"], "feasibility.economics.investmentCosts": ["κόστη επένδυσης", "investment costs"],
  "feasibility.economics.ppa": ["PPA", "PPA"], "feasibility.economics.equipmentCostOverride": ["τιμές εξοπλισμού του έργου", "project equipment prices"],
};

/** Which inputs of the sizing differ between a run and the project now, in
 * the reader's words, so the "previous inputs" notice can say what changed. */
export function sizingInputsChanged(inputConfig, cfg, isGreek) {
  const a = sizingInputs(inputConfig), b = sizingInputs(cfg);
  if (!a || !b) return [];
  const names = [];
  const compare = (prefix, x, y) => {
    for (const key of new Set([...Object.keys(x || {}), ...Object.keys(y || {})])) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (sizingInputKey(x?.[key]) === sizingInputKey(y?.[key])) continue;
      if ((path === "feasibility" || path === "feasibility.economics") && typeof x?.[key] === "object") { compare(path, x?.[key], y?.[key]); continue; }
      const label = INPUT_GROUP_LABELS[path];
      names.push(label ? label[isGreek ? 0 : 1] : key);
    }
  };
  compare("", a, b);
  return [...new Set(names)];
}

export function normalizeAutodesign(saved) {
  const a = saved && typeof saved === "object" ? saved : {};
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== "" && v !== null ? Number(v) : d);
  return {
    dcAcBand: num(a.dcAcBand, 0.1),
    lockModule: a.lockModule === true,
    lockInverter: a.lockInverter === true,
    ownedOnly: a.ownedOnly === true,
    moduleManufacturers: Array.isArray(a.moduleManufacturers) ? a.moduleManufacturers : [],
    inverterManufacturers: Array.isArray(a.inverterManufacturers) ? a.inverterManufacturers : [],
    moduleTechnologies: Array.isArray(a.moduleTechnologies) ? a.moduleTechnologies : [],
    moduleFaces: ['monofacial', 'bifacial'].includes(a.moduleFaces) ? a.moduleFaces : 'all',
    inverterPhases: ['one', 'three', 'both'].includes(a.inverterPhases) ? a.inverterPhases : 'both',
    inverterConversionModel: ['sandia', 'pvwatts'].includes(a.inverterConversionModel) ? a.inverterConversionModel : '',
    alternativesPerSize: Math.max(1, Math.min(3, Math.round(num(a.alternativesPerSize, 3)))),
    driftTolerancePct: num(a.driftTolerancePct, 8),
    autoContinue: a.autoContinue !== false,
  };
}

// The auto-design request the sizing worker completes per candidate: the
// project's site and equipment, never the auto-design dialogue's own
// parameters. The capacity and the DC/AC window are set by the worker.
export function buildAutodesignPayload(cfg) {
  const auto = withEquipmentMode(normalizeSizingSettings(cfg?.pvSizing, cfg), cfg).autodesign;
  const po = cfg?.panelOrientation || {};
  const rows = Number(cfg?.pv_design?.spacingInfo?.row_vertical_modules) || 1;
  return {
    lat: cfg?.loc_info?.lat ?? cfg?.lat ?? null,
    lon: cfg?.loc_info?.lon ?? cfg?.lon ?? null,
    roof_area: cfg?.roof_area ?? null,
    size_mode: "manual",
    slope_tilt: cfg?.loc_info?.tilt ?? null,
    slope_azimuth: cfg?.loc_info?.azimuth ?? null,
    module: auto.lockModule ? cfg?.read_module : undefined,
    inverter: auto.lockInverter ? cfg?.read_inverter : undefined,
    tilt: Number.isFinite(Number(po.tilt)) && po.tilt !== "" && po.tilt !== null ? Number(po.tilt) : 25,
    azimuth: Number.isFinite(Number(po.azim)) && po.azim !== "" && po.azim !== null ? Number(po.azim) : 180,
    mount_type: 'fixed',
    surface_type: 'grass',
    layout: { min_tiers: rows, max_tiers: rows, fit_to_parcel: Boolean(cfg?.loc_info?.parcel), parcel: cfg?.loc_info?.parcel || null, whole_strings_per_row: false },
  };
}

export function missingAutodesignInputs(payload, isGreek) {
  const missing = [];
  if (payload.lat == null || payload.lon == null) missing.push(isGreek ? "τοποθεσία" : "location");
  if (payload.roof_area == null) missing.push(isGreek ? "διαθέσιμη επιφάνεια" : "available area");
  if (payload.tilt == null || payload.azimuth == null) missing.push(isGreek ? "κλίση/προσανατολισμός" : "tilt/azimuth");
  return missing;
}


// What a stage-2 row says when a size could not be designed or evaluated.
// Known codes get a sentence; anything else is the engine's own reason,
// shortened — never a generic "retry", which hides the cause.
// Why the auto-design found no plant (the engine's blocking reason). Mirrors
// DESIGN_BLOCKING in api/services/pv_sizing_report.py.
export const DESIGN_BLOCKING_REASONS = {
  grouped_capacity_outside_window: ["η εφικτή διάταξη αποκλίνει περισσότερο από την επιτρεπτή ανοχή ισχύος", "the feasible layout exceeds the allowed capacity tolerance"],
  grouped_dc_ac_range: ["δεν βρέθηκε ακέραιος συνδυασμός αντιστροφέων και στοιχειοσειρών εντός του εύρους DC/AC", "no whole-number combination of inverters and strings fits the DC/AC range"],
  grouped_dc_ac_ratio: ["η ηλεκτρικά εφικτή διάταξη δεν ικανοποιεί τον επιλεγμένο λόγο DC/AC", "the electrically feasible layout does not satisfy the selected DC/AC ratio"],
  grouped_string_limits: ["δεν προκύπτει επιτρεπτό μήκος στοιχειοσειράς από τα όρια τάσης", "the voltage limits do not allow a valid string length"],
  grouped_mppt_capacity: ["οι είσοδοι MPPT δεν επαρκούν για τη διάταξη", "the MPPT inputs cannot accommodate the layout"],
  grouped_invalid_mppt_limits: ["τα δεδομένα ορίων MPPT του αντιστροφέα είναι ελλιπή ή ασυνεπή", "the inverter MPPT limits are incomplete or inconsistent"],
  grouped_missing_module_power: ["λείπει η ισχύς του πλαισίου από τον κατάλογο", "module power is missing from the catalogue"],
  grouped_missing_module_electrics: ["λείπουν ηλεκτρικά χαρακτηριστικά του πλαισίου", "module electrical characteristics are missing"],
  grouped_missing_inverter_params: ["λείπουν απαιτούμενα χαρακτηριστικά του αντιστροφέα", "required inverter characteristics are missing"],
  vac: ["καμία τάση εξόδου αντιστροφέα του καταλόγου δεν ταιριάζει με τη σύνδεση του έργου", "no catalogue inverter's AC voltage matches the project's connection"],
  phase: ["κανένας αντιστροφέας με τις φάσεις που επιλέχθηκαν", "no inverter with the selected phases"],
  paco_threshold: ["οι αντιστροφείς του καταλόγου είναι πολύ μικροί για αυτή την ισχύ", "the catalogue inverters are too small for this size"],
  insufficient_area: ["δεν χωρά στη διαθέσιμη επιφάνεια", "does not fit the available area"],
  parcel_cannot_hold_design: ["δεν χωρά στο γεωτεμάχιο", "does not fit the parcel"],
  capacity_outside_oversize_window: ["δεν προκύπτει ισχύς κοντά στον στόχο με ακέραια πλαίσια", "no size near the target in whole modules"],
  pre_allocation_electrical: ["καμία συμβατή διάταξη στοιχειοσειρών (λόγος DC/AC, τάσεις στοιχειοσειρών ή είσοδοι MPPT)", "no compatible string layout (DC/AC ratio, string voltages or MPPT inputs)"],
  layout_gcr_invalid: ["μη έγκυρη κάλυψη εδάφους για το πλαίσιο", "invalid ground coverage for the module"],
  search_elapsed_limit: ["η αναζήτηση σταμάτησε στο όριο χρόνου πριν εξετάσει όλο τον κατάλογο", "the search stopped at its time limit before the whole catalogue was examined"],
  search_allocation_limit: ["η αναζήτηση σταμάτησε στο όριο υπολογισμών πριν εξετάσει όλο τον κατάλογο", "the search stopped at its computation limit before the whole catalogue was examined"],
  search_candidate_group_limit: ["η αναζήτηση σταμάτησε στο όριο συνδυασμών πριν εξετάσει όλο τον κατάλογο", "the search stopped at its combination limit before the whole catalogue was examined"],
  search_module_share: ["η αναζήτηση μοίρασε τον διαθέσιμο χρόνο και τους υπολογισμούς στα πλαίσια και δεν εξέτασε όλους τους συνδυασμούς", "the search shared its time and computation budget across the modules and did not examine every combination"],
};

export function designFailureText(message, isGreek, fmt = (v) => String(Math.round(v)), diagnostics = null) {
  const reason = DESIGN_BLOCKING_REASONS[diagnostics?.blocking_reason];
  if (reason) return `${isGreek ? "δεν βρέθηκε σχεδιασμός" : "no design found"}: ${reason[isGreek ? 0 : 1]}`;
  const raw = String(message || "");
  const area = raw.match(/^PV_SIZING_AREA_EXCEEDED:([\d.]+)$/);
  if (area) {
    const cap = fmt(Number(area[1]));
    return isGreek ? `δεν χωρά στη διαθέσιμη επιφάνεια (χωρούν περίπου ${cap} kWp)` : `does not fit the available area (about ${cap} kWp fit)`;
  }
  // Which constraint, with its numbers (pv_sizing_run): the old code named three at once.
  const over = raw.match(/^PV_SIZING_OVER_BUDGET:([\d.]+):([\d.]+)$/);
  if (over) {
    const eur = (v) => `${Number(v).toLocaleString(isGreek ? "el-GR" : "en-GB", { maximumFractionDigits: 0 })} €`;
    return isGreek ? `η σχεδίαση κοστίζει ${eur(over[1])}, πάνω από τον προϋπολογισμό ${eur(over[2])} (κοστολόγηση με τις τιμές του εξοπλισμού της)`
      : `the design costs ${eur(over[1])}, over the ${eur(over[2])} budget (priced at its equipment's prices)`;
  }
  const out = raw.match(/^PV_SIZING_DESIGNED_OUTSIDE_RANGE:([\d.]+):([\d.]+):([\d.]+)$/);
  if (out) {
    return isGreek ? `η σχεδίαση βγήκε ${fmt(Number(out[1]))} kWp, εκτός του εύρους ${fmt(Number(out[2]))}–${fmt(Number(out[3]))} kWp`
      : `the design came out at ${fmt(Number(out[1]))} kWp, outside the ${fmt(Number(out[2]))}–${fmt(Number(out[3]))} kWp range`;
  }
  if (raw === "PV_SIZING_DESIGNED_OUTSIDE_CONSTRAINTS") return isGreek ? "η σχεδιασμένη εγκατάσταση βγήκε εκτός των περιορισμών (DC/AC, όριο έγχυσης ή προϋπολογισμός)" : "the designed plant fell outside the constraints (DC/AC, injection limit or budget)";
  if (ERROR_TEXTS[raw]) return ERROR_TEXTS[raw][isGreek ? 0 : 1];
  // The auto-design's own sentences (BESS/PV_output/system_design.py).
  const phases = raw.match(/^Inverter (.+) has (\S+) phases; the selected phase filter allows ([\d, ]+)\.?$/);
  if (phases) {
    const word = (p) => ({ 1: ["μονοφασικός", "single-phase"], 3: ["τριφασικός", "three-phase"] })[String(p).trim()] || [isGreek ? "με άγνωστο αριθμό φάσεων" : "of unknown phase count", "of unknown phase count"];
    const allowed = phases[3].split(",").map((p) => p.trim()).filter(Boolean);
    const allowedText = allowed.map((p) => {
      const w = word(p)[isGreek ? 0 : 1];
      return isGreek ? w.replace(/ός$/, "οί") : w;
    }).join(isGreek ? " ή " : " or ");
    return isGreek
      ? `ο αντιστροφέας ${phases[1]} είναι ${word(phases[2])[0]}, ενώ για αυτό το μέγεθος επιτρέπονται μόνο ${allowedText} αντιστροφείς`
      : `inverter ${phases[1]} is ${word(phases[2])[1]}, while only ${allowedText} inverters are allowed for this size`;
  }
  if (/No proposed design of the system was found/i.test(raw)) {
    return isGreek ? "δεν βρέθηκε σχεδίαση με τον εξοπλισμό και τα δεδομένα του έργου" : "no design was found with the project's equipment and data";
  }
  if (/not enough area|do not fit|cannot fit|roof area/i.test(raw)) return isGreek ? `δεν χωρά στη διαθέσιμη επιφάνεια — ${raw.slice(0, 160)}` : `does not fit the available area — ${raw.slice(0, 160)}`;
  if (/no inverter|no module|catalog|catalogue/i.test(raw)) return isGreek ? `δεν βρέθηκε κατάλληλος εξοπλισμός — ${raw.slice(0, 160)}` : `no suitable equipment — ${raw.slice(0, 160)}`;
  return raw ? raw.slice(0, 200) : (isGreek ? "ο σχεδιασμός απέτυχε χωρίς αιτία από τη μηχανή" : "the design failed without a reason from the engine");
}

export const WARNING_TEXTS = {
  search_resolution: (w, isGreek) => (isGreek
    ? "Ο έλεγχος με πυκνότερο πλέγμα βρήκε καλύτερη λύση, η οποία χρησιμοποιήθηκε. Η αναζήτηση δεν πιστοποιεί καθολικό βέλτιστο· επιβεβαιώστε με το Στάδιο 2."
    : "The denser-grid check found a better solution, which was retained. The search does not certify a global optimum; confirm with Stage 2."),
  profile_use_case_mismatch: (w, isGreek, fmt) => (isGreek
    ? `Το προφίλ κατανάλωσης (${fmt(w.annual_load_kwh)} kWh/έτος) δεν ταιριάζει στη χρήση «${w.use_case}». Ελέγξτε την εισαγωγή ή τη χρήση στις Τεχνικές Παραδοχές· όλη η ανάλυση εξαρτάται από αυτό.`
    : `The consumption profile (${fmt(w.annual_load_kwh)} kWh/yr) does not match the “${w.use_case}” use case. Check the import or the use case in the Technical Assumptions; the whole analysis depends on it.`),
  area_too_small: (w, isGreek, fmt) => (isGreek
    ? `Η διαθέσιμη επιφάνεια χωρά λιγότερο από ${fmt(w.area_cap_kwp)} kWp.`
    : `The available area holds less than ${fmt(w.area_cap_kwp)} kWp.`),
  typical_equipment_price: (w, isGreek) => (isGreek
    ? "Το έργο δεν έχει επιλεγμένο πλαίσιο ή αντιστροφέα με τιμή και ο κατάλογος δεν έδωσε τιμές: το Στάδιο 1 κοστολογεί τον εξοπλισμό με τυπική τιμή αγοράς (0,11 €/Wp πλαίσια, 0,05 €/W AC αντιστροφείς). Το Στάδιο 2 κοστολογεί κάθε σχεδίαση με τον δικό της εξοπλισμό."
    : "The project has no priced module or inverter and the catalogue gave no prices: stage 1 prices the equipment at a typical market price (0.11 €/Wp modules, 0.05 €/W AC inverters). Stage 2 prices each design with its own equipment."),
  area_limited: (w, isGreek, fmt) => (isGreek
    ? `Η βέλτιστη ισχύς (${fmt(w.k_kwp)} kWp) βρίσκεται στο εκτιμώμενο όριο επιφάνειας (${fmt(w.area_cap_kwp)} kWp). Η εκτίμηση από απόδοση πλαισίου × κάλυψη εδάφους δεν επιβεβαιώνει χωροθέτηση· απαιτείται έλεγχος διαδρόμων, αποστάσεων και εμποδίων.`
    : `The optimum (${fmt(w.k_kwp)} kWp) is at the estimated area limit (${fmt(w.area_cap_kwp)} kWp). Module efficiency × ground coverage is not a confirmed layout; access paths, setbacks and obstacles must be checked.`),
  stage_disagreement: (w, isGreek, fmt) => (isGreek
    ? `Στη βέλτιστη ισχύ (${fmt(w.k_kwp)} kWp) η ταχεία εκτίμηση δίνει εξοικονόμηση έτους 1 ${fmt(w.kernel_year1_saving_eur)} € ενώ η πλήρης εκκαθάριση της μηχανής ${fmt(w.engine_year1_saving_eur)} € (${w.deviation_pct > 0 ? "+" : "−"}${fmt(Math.abs(w.deviation_pct))} %). Η καμπύλη NPV είναι ενδεικτική· υπερισχύει η κατάταξη του Σταδίου 2.`
    : `At the optimum (${fmt(w.k_kwp)} kWp) the fast estimate gives a year-1 saving of ${fmt(w.kernel_year1_saving_eur)} € while the engine's full settlement gives ${fmt(w.engine_year1_saving_eur)} € (${w.deviation_pct > 0 ? "+" : "−"}${fmt(Math.abs(w.deviation_pct))} %). The NPV curve is indicative; the stage-2 ranking prevails.`),
};

// Which warnings are notes about the answer rather than doubts about the inputs.
export const INFO_WARNINGS = new Set(["area_limited", "typical_equipment_price"]);

const PRICE_SOURCES = {
  project: ["του έργου", "the project's"],
  override: ["η τιμή που ορίστηκε στο έργο", "the price entered in the project"],
  catalogue: ["διάμεση τιμή καταλόγου", "catalogue median"],
  sizing: ["τιμές της διαστασιολόγησης", "the sizing's prices"],
  typical: ["τυπική τιμή αγοράς", "typical market price"],
};

/**
 * How the automatic search range is derived (pv_core/pv_sizing.auto_range), as
 * lines for the "i" next to it: the rule always, and the arithmetic once a run
 * has given the annual load and the reference yield.
 */
/** The three ceilings the automatic range weighs, and which one set the
 * upper end: the load (the run's share of the annual consumption, 200 % by
 * default), the area (a preliminary estimate) or the budget (the stage-1
 * cost curve). */
export function autoRangeLimits(s1) {
  if (!s1?.range) return null;
  const auto = s1.settings?.autoRange || {};
  const load = s1.site?.annual_load_kwh, yieldKwhPerKwp = s1.reference?.annual_yield_kwh_per_kwp;
  const loadPct = Number(s1.settings?.autoMaxLoadPct) > 0 ? Number(s1.settings.autoMaxLoadPct) : 200;
  const loadKwp = load > 0 && yieldKwhPerKwp > 0 ? Math.max(0.5, loadPct / 100 * load / yieldKwhPerKwp) : null;
  const areaKwp = Number.isFinite(Number(auto.area_cap_kwp)) ? Number(auto.area_cap_kwp) : null;
  const budgetEur = Number.isFinite(Number(s1.settings?.budgetEur)) && s1.settings?.budgetEur !== null && s1.settings?.budgetEur !== "" ? Number(s1.settings.budgetEur) : null;
  const budgetKwp = s1.budget_limited && budgetEur !== null ? s1.range.k_max_kwp : null;
  const limitedBy = budgetKwp !== null ? "budget" : auto.limited_by === "area" && areaKwp !== null ? "area" : "load";
  return { loadKwp, loadPct, areaKwp, budgetEur, budgetKwp, limitedBy, lowerFromArea: Boolean(auto.lower_from_area) };
}

/** What cut the upper end of the LAST run's range, for the manual range too,
 * where the two fields alone say nothing about it: the budget (the search then
 * stopped short of the max typed in - range.k_max_kwp vs k_max_requested_kwp)
 * and the area (an estimate the search does not apply, but stage 2 does: sizes
 * above it come back infeasible). Null when nothing was cut. */
export function rangeCuts(s1) {
  if (!s1?.range) return null;
  const n = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  const requested = n(s1.range.k_max_requested_kwp), searched = n(s1.range.k_max_kwp);
  const budgetEur = n(s1.settings?.budgetEur);
  const budget = s1.budget_limited && requested !== null && searched !== null && searched < requested - 1e-6
    ? { requestedKwp: requested, searchedKwp: searched, budgetEur } : null;
  const areaCapKwp = n(s1.settings?.areaCapKwp ?? s1.settings?.autoRange?.area_cap_kwp);
  const top = requested ?? searched;
  const area = areaCapKwp !== null && top !== null && top > areaCapKwp * 1.001 ? { maxKwp: top, areaCapKwp } : null;
  return budget || area ? { budget, area } : null;
}

/** How the stage-1 €/kWp was built from the project's costs (api/services/pv_sizing_cost.py). */
export function costBreakdownText(detail, isGreek, fmt) {
  if (!detail || !detail.parts) return null;
  if (detail.eurPerKwpSource === "sizing") {
    return isGreek ? "Ενιαίο κόστος: το €/kWp περιλαμβάνει εξοπλισμό, εγκατάσταση, λοιπά και μετασχηματιστή· οι τιμές εξοπλισμού μετρούν μόνο για τη συντήρηση."
      : "Single cost: the €/kWp holds equipment, installation, other items and the transformer; the equipment prices count only for O&M.";
  }
  if (detail.mode === "simplified") {
    return isGreek ? `Από το έργο (απλοποιημένη κοστολόγηση): ${fmt(detail.projectEurPerKwp, 0)} €/kWp πλήρως εγκατεστημένου ΦΒ.`
      : `From the project (simplified costing): ${fmt(detail.projectEurPerKwp, 0)} €/kWp of installed PV.`;
  }
  const p = detail.prices || {};
  const src = (key) => (PRICE_SOURCES[key] || [key, key])[isGreek ? 0 : 1];
  const sources = p.moduleEurPerWpSource === p.inverterEurPerWacSource ? src(p.moduleEurPerWpSource)
    : `${isGreek ? "πλαίσια" : "modules"}: ${src(p.moduleEurPerWpSource)}, ${isGreek ? "αντιστροφείς" : "inverters"}: ${src(p.inverterEurPerWacSource)}`;
  const trf = detail.transformer ? (isGreek ? ` + μετασχηματιστής ${fmt(detail.transformer.eurPerKva, 0)} €/kVA` : ` + transformer ${fmt(detail.transformer.eurPerKva, 0)} €/kVA`) : "";
  return isGreek
    ? `Από το έργο: εξοπλισμός ${fmt(detail.parts.hardware, 0)} €/kWp (πλαίσια ${fmt(p.moduleEurPerWp, 3)} €/Wp, αντιστροφείς ${fmt(p.inverterEurPerWac, 3)} €/W AC · ${sources}) + εγκατάσταση ${fmt(detail.parts.installation, 0)} €/kWp + λοιπά ${fmt(detail.parts.bos, 0)} €/kWp = ${fmt(detail.projectEurPerKwp, 0)} €/kWp${trf}. Στο Στάδιο 2 κάθε σχεδίαση κοστολογείται με τον δικό της εξοπλισμό.`
    : `From the project: equipment ${fmt(detail.parts.hardware, 0)} €/kWp (modules ${fmt(p.moduleEurPerWp, 3)} €/Wp, inverters ${fmt(p.inverterEurPerWac, 3)} €/W AC · ${sources}) + installation ${fmt(detail.parts.installation, 0)} €/kWp + other ${fmt(detail.parts.bos, 0)} €/kWp = ${fmt(detail.projectEurPerKwp, 0)} €/kWp${trf}. In stage 2 each design is priced with its own equipment.`;
}

/** What "from the project economics" means for this project, in a few
 * words, from the config alone: the simplified €/kWp, or the two equipment
 * prices of the detailed costing. Null when the costing has no prices yet. */
export function projectCostingSummary(cfg, isGreek) {
  const inv = cfg?.feasibility?.economics?.investmentCosts || {};
  const t = (el, en) => (isGreek ? el : en);
  const n = (v, d = 0) => fmtNum(Number(v), isGreek, d);
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== "" && Number.isFinite(Number(v)) && Number(v) > 0;
  if (String(inv.costingMode || "detailed").toLowerCase() === "simplified") {
    return has(inv.simplePvEurPerKwp) ? `${n(inv.simplePvEurPerKwp)} €/kWp ${t("πλήρως εγκατεστημένο", "turnkey")}` : null;
  }
  const parts = [has(inv.moduleEurPerWdc) ? `${t("πλαίσια", "modules")} ${n(inv.moduleEurPerWdc, 2)} €/Wp` : null,
    has(inv.inverterEurPerWdc) ? `${t("αντιστροφείς", "inverters")} ${n(inv.inverterEurPerWdc, 2)} €/W` : null].filter(Boolean);
  return parts.length ? `${parts.join(" · ")} ${t("+ εγκατάσταση και λοιπά", "+ installation and other items")}` : null;
}

export function warningText(w, isGreek, fmt = (v) => String(Math.round(v))) {
  const f = WARNING_TEXTS[w?.code];
  return f ? f(w, isGreek, fmt) : String(w?.code || "");
}
